import { useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import {
    buildSetupData,
    buildParkingData,
    buildGPSProjection,
    pixelToGPS,
    SubmissionGPSPoint,
} from "../utils/yardSubmission";

import { submitYard, updateYard, getYardLayout, getOriginalYardImage, uploadOriginalYardImage, deleteYard } from "../services/yard.service";

import {
    loadYardConfiguration,
    loadYardGeometry,
    loadParkingSpaces,
} from "../utils/yardLoader";


type YardInformation = {
    name: string;
    address: string;
    zipCode: string;
    altitudeA: string;
    altitudeB: string;
    altitudeC: string;
};

type GPSPoint = {
    x: number;
    y: number;
    lat: number;
    lon: number;
};

type GPSInput = {
    lat: string;
    lon: string;
};

type GeoCalibration = {
    originLat: number;
    originLon: number;
    pixelsToLocal: number[][];
    localToPixels: number[][];
};

type SetupStep =
    | "yard-info"
    | "load-image"
    | "ready"
    | "gps-p1"
    | "gps-p2"
    | "gps-p3"
    | "gps-p4"
    | "geofence"
    | "scale"
    | "complete";

const setupSteps = [
    { key: "yard-info" as SetupStep, number: "1", label: "Yard Information" },
    { key: "load-image" as SetupStep, number: "2", label: "Load Image" },
    { key: "gps-p1" as SetupStep, number: "3", label: "GPS Point P1" },
    { key: "gps-p2" as SetupStep, number: "4", label: "GPS Point P2" },
    { key: "gps-p3" as SetupStep, number: "5", label: "GPS Point P3" },
    { key: "gps-p4" as SetupStep, number: "6", label: "GPS Point P4" },
    { key: "geofence" as SetupStep, number: "7", label: "Draw Geofence" },
    { key: "scale" as SetupStep, number: "8", label: "Calibrate Scale" },
    { key: "complete" as SetupStep, number: "9", label: "Complete Setup" },
];

function solveLinearSystem(A: number[][], b: number[]): number[] {
    const n = b.length;
    const M = A.map((row, i) => [...row, b[i]]);

    for (let col = 0; col < n; col++) {
        let pivot = col;

        for (let row = col + 1; row < n; row++) {
            if (Math.abs(M[row][col]) > Math.abs(M[pivot][col])) {
                pivot = row;
            }
        }

        if (Math.abs(M[pivot][col]) < 1e-12) {
            throw new Error("GPS calibration points produce an invalid transformation.");
        }

        [M[col], M[pivot]] = [M[pivot], M[col]];

        const divisor = M[col][col];

        for (let j = col; j <= n; j++) {
            M[col][j] /= divisor;
        }

        for (let row = 0; row < n; row++) {
            if (row === col) continue;

            const factor = M[row][col];

            for (let j = col; j <= n; j++) {
                M[row][j] -= factor * M[col][j];
            }
        }
    }

    return M.map((row) => row[n]);
}

function computeProjectiveTransform(
    source: [number, number][],
    target: [number, number][]
): number[][] {
    if (source.length !== 4 || target.length !== 4) {
        throw new Error("Exactly four calibration points are required.");
    }

    const A: number[][] = [];
    const b: number[] = [];

    for (let i = 0; i < 4; i++) {
        const [x, y] = source[i];
        const [u, v] = target[i];

        A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
        b.push(u);

        A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
        b.push(v);
    }

    const h = solveLinearSystem(A, b);

    return [
        [h[0], h[1], h[2]],
        [h[3], h[4], h[5]],
        [h[6], h[7], 1],
    ];
}

function latLonToLocalFeet(
    lat: number,
    lon: number,
    originLat: number,
    originLon: number
): [number, number] {
    const earthRadiusFeet = 20925524.9;

    const latRad = (lat * Math.PI) / 180;
    const originLatRad = (originLat * Math.PI) / 180;

    const north =
        (latRad - originLatRad) * earthRadiusFeet;

    const east =
        ((lon - originLon) * Math.PI / 180) *
        earthRadiusFeet *
        Math.cos(originLatRad);

    return [east, north];
}

function computeGeoCalibration(
    points: GPSPoint[]
): GeoCalibration {
    if (points.length !== 4) {
        throw new Error("All four GPS points are required.");
    }

    // Four calibration anchors must cover a meaningful two-dimensional area.
    // Nearly collinear points create unstable GPS projections.
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const width = Math.max(...xs) - Math.min(...xs);
    const height = Math.max(...ys) - Math.min(...ys);

    if (width < 1 || height < 1) {
        throw new Error(
            "GPS calibration points must be spread across the yard image."
        );
    }

    let maximumTriangleArea2 = 0;

    for (let i = 0; i < points.length; i++) {
        for (let j = i + 1; j < points.length; j++) {
            for (let k = j + 1; k < points.length; k++) {
                const a = points[i];
                const b = points[j];
                const c = points[k];

                const area2 = Math.abs(
                    (b.x - a.x) * (c.y - a.y) -
                    (b.y - a.y) * (c.x - a.x)
                );

                maximumTriangleArea2 = Math.max(
                    maximumTriangleArea2,
                    area2
                );
            }
        }
    }

    if (maximumTriangleArea2 / (width * height) < 0.05) {
        throw new Error(
            "GPS calibration points are nearly collinear. Select four widely separated locations around the yard."
        );
    }

    const originLat =
        points.reduce((sum, p) => sum + p.lat, 0) / points.length;

    const originLon =
        points.reduce((sum, p) => sum + p.lon, 0) / points.length;

    const pixelPoints: [number, number][] =
        points.map((p) => [p.x, p.y]);

    const localPoints: [number, number][] =
        points.map((p) =>
            latLonToLocalFeet(
                p.lat,
                p.lon,
                originLat,
                originLon
            )
        );

    const pixelsToLocal =
        computeProjectiveTransform(pixelPoints, localPoints);

    const localToPixels =
        computeProjectiveTransform(localPoints, pixelPoints);

    return {
        originLat,
        originLon,
        pixelsToLocal,
        localToPixels,
    };
}

function snapEndpointDegrees(
    start: [number, number],
    end: [number, number],
    increment: number
): [number, number] {
    const [x0, y0] = start;
    const [x1, y1] = end;

    const dx = x1 - x0;
    const dy = y1 - y0;

    const length = Math.hypot(dx, dy);

    if (length < 1e-9) {
        return end;
    }

    const rawAngle =
        Math.atan2(dy, dx) * (180 / Math.PI);

    const snappedAngle =
        Math.round(rawAngle / increment) * increment;

    const radians =
        snappedAngle * (Math.PI / 180);

    return [
        x0 + Math.cos(radians) * length,
        y0 + Math.sin(radians) * length,
    ];
}

function snapPolygonPoint15(
    points: [number, number][],
    point: [number, number]
): [number, number] {
    if (points.length === 0) {
        return point;
    }

    return snapEndpointDegrees(
        points[points.length - 1],
        point,
        15
    );
}

function calculatePolygonAreaPx(
    points: [number, number][]
): number {
    if (points.length < 3) {
        return 0;
    }

    let area = 0;

    for (let i = 0; i < points.length; i++) {
        const [x1, y1] = points[i];
        const [x2, y2] =
            points[(i + 1) % points.length];

        area += x1 * y2 - x2 * y1;
    }

    return Math.abs(area) / 2;
}

type ParkingSpace = {
    id: string;
    label: string;
    anchor: [number, number];
    angle: number;
    lengthFt: number;
    widthFt: number;
};

type DrawingTool =
    | "pan"
    | "select"
    | "parking"
    | "section"
    | "building"
    | "no-parking"
    | "crosswalk"
    | "pod"
    | "scanner"
    | "overflow";

type YardSection = {
    id: string;
    label: string;
    rect: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    entry: [number, number];
    orientationDeg: number;
};

type YardBuilding = {
    id: string;
    label: string;
    points: [number, number][];
};

type YardPod = {
    px: [number, number];
};

type YardScanner = {
    id: string;
    px: [number, number];
    orientationDeg: number;
};

type OverflowBlock = {
    id: string;
    label: string;
    points: [number, number][];
    orientationDeg: number;
    active: boolean;
};

type NoParkingZone = {
    id: string;
    points: [number, number][];
};

type Crosswalk = {
    id: string;
    p1: [number, number];
    p2: [number, number];
    widthFt: number;
};

type SelectableYardObject =
    | { type: "parking"; id: string }
    | { type: "gps-point"; id: string }
    | { type: "geofence-vertex"; id: string }
    | { type: "section"; id: string }
    | { type: "building"; id: string }
    | { type: "pod"; id: string }
    | { type: "scanner"; id: string }
    | { type: "crosswalk"; id: string }
    | { type: "no-parking"; id: string }
    | { type: "overflow"; id: string };

type SelectionBox = {
    start: [number, number];
    end: [number, number];
};

type YardObjectDeletionSnapshot = {
    gpsPoints: (GPSPoint | null)[];
    gpsInputs: GPSInput[];
    geoCalibration: GeoCalibration | null;
    geofencePoints: [number, number][];
    geofenceComplete: boolean;
    sections: YardSection[];
    buildings: YardBuilding[];
    pod: YardPod | null;
    scanners: YardScanner[];
    crosswalks: Crosswalk[];
    noParkingZones: NoParkingZone[];
    overflowBlocks: OverflowBlock[];
};

type UndoAction =
    | { type: "move-gps-point"; points: (GPSPoint | null)[]; calibration: GeoCalibration | null }
    | { type: "delete-yard-object"; snapshot: YardObjectDeletionSnapshot }
    | {
          type: "add-parking";
          spaces: ParkingSpace[];
      }
    | {
          type: "delete-parking";
          spaces: ParkingSpace[];
      }
    | {
          type: "move-parking";
          spaces: ParkingSpace[];
      }
    | {
          type: "add-section";
          section: YardSection;
      }
    | {
          type: "add-no-parking";
          zone: NoParkingZone;
      }
    | {
          type: "add-building";
          building: YardBuilding;
      }
    | {
          type: "add-crosswalk";
          crosswalk: Crosswalk;
      };

export default function YardDesigner(): React.ReactElement {
    const navigate = useNavigate();

    const [setupStep, setSetupStep] = useState<SetupStep>("load-image");
    const [yardInfo, setYardInfo] = useState<YardInformation>({
        name: "",
        address: "",
        zipCode: "",
        altitudeA: "30",
        altitudeB: "50",
        altitudeC: "2",
    });

    const [yardInfoError, setYardInfoError] = useState("");

    const [yardImage, setYardImage] = useState<string | null>(null);
    const [yardImageName, setYardImageName] = useState("");
    // True only when setup.json was saved but image upload failed.
    const [imageUploadNeedsRetry, setImageUploadNeedsRetry] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageContainerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const [gpsInputs, setGpsInputs] = useState<GPSInput[]>([
        { lat: "", lon: "" },
        { lat: "", lon: "" },
        { lat: "", lon: "" },
        { lat: "", lon: "" },
    ]);

    const [gpsPoints, setGpsPoints] = useState<(GPSPoint | null)[]>([
        null,
        null,
        null,
        null,
    ]);

    const [gpsError, setGpsError] = useState("");
    const [geoCalibration, setGeoCalibration] =
        useState<GeoCalibration | null>(null);

    const [geofencePoints, setGeofencePoints] =
        useState<[number, number][]>([]);

    const [geofenceComplete, setGeofenceComplete] =
        useState(false);

    const [geofenceError, setGeofenceError] =
        useState("");

    const [geofencePreview, setGeofencePreview] =
        useState<[number, number] | null>(null);

    // Trailer scale calibration.
    // During setup the user drags along a known 53-ft trailer.
    const [scaleDragStart, setScaleDragStart] =
        useState<[number, number] | null>(null);

    // Ref tracks the active drag immediately.
    // State is still used to render the measurement overlay.
    const scaleDragStartRef =
        useRef<[number, number] | null>(null);

    const [scaleDragEnd, setScaleDragEnd] =
        useState<[number, number] | null>(null);

    const [scalePxLength, setScalePxLength] =
        useState<number | null>(null);

    const [scaleFeet, setScaleFeet] =
        useState("53");

    const [metersPerPx, setMetersPerPx] =
        useState<number | null>(null);

    const [scaleError, setScaleError] =
        useState("");

    // Add Parking
    const [drawingTool, setDrawingTool] =
        useState<DrawingTool>("select");

    const [parkingAnchor, setParkingAnchor] =
        useState<[number, number] | null>(null);

    const [parkingPreviewAngle, setParkingPreviewAngle] =
        useState<number | null>(null);

    const [parkingSpaces, setParkingSpaces] =
        useState<ParkingSpace[]>([]);

    const [parkingError, setParkingError] =
        useState("");

    // Canvas zoom
    const [zoom, setZoom] =
        useState(1);

    const [zoomOffset, setZoomOffset] =
        useState({ x: 0, y: 0 });

    // Suppress the click generated when a pan gesture ends.
    const suppressPanClickRef = useRef(false);

    // Pan navigation
    const panDragRef = useRef<{
        x: number;
        y: number;
        offsetX: number;
        offsetY: number;
    } | null>(null);

    // Sections
    const [sections, setSections] =
        useState<YardSection[]>([]);

    const [sectionDragStart, setSectionDragStart] =
        useState<[number, number] | null>(null);

    const [sectionDragEnd, setSectionDragEnd] =
        useState<[number, number] | null>(null);

    const sectionDragStartRef =
        useRef<[number, number] | null>(null);

    // Buildings
    const [buildings, setBuildings] =
        useState<YardBuilding[]>([]);

    const [buildingPoints, setBuildingPoints] =
        useState<[number, number][]>([]);

    const [buildingPreview, setBuildingPreview] =
        useState<[number, number] | null>(null);

    // Pod
    const [pod, setPod] =
        useState<YardPod | null>(null);

    const [isDraggingPod, setIsDraggingPod] =
        useState(false);

    const podDragStartRef =
        useRef<[number, number] | null>(null);

    // Scanner
    const [scanners, setScanners] =
        useState<YardScanner[]>([]);

    const [scannerPlacementAnchor, setScannerPlacementAnchor] =
        useState<[number, number] | null>(null);

    const [scannerPreviewAngle, setScannerPreviewAngle] =
        useState(0);

    // Overflow
    const [overflowBlocks, setOverflowBlocks] =
        useState<OverflowBlock[]>([]);

    const [overflowPoints, setOverflowPoints] =
        useState<[number, number][]>([]);

    const [overflowPreview, setOverflowPreview] =
        useState<[number, number] | null>(null);

    // Crosswalks
    const [crosswalks, setCrosswalks] =
        useState<Crosswalk[]>([]);

    const [crosswalkStart, setCrosswalkStart] =
        useState<[number, number] | null>(null);

    const [crosswalkPreview, setCrosswalkPreview] =
        useState<[number, number] | null>(null);

    // No-Parking zones
    const [noParkingZones, setNoParkingZones] =
        useState<NoParkingZone[]>([]);

    const [noParkingPoints, setNoParkingPoints] =
        useState<[number, number][]>([]);

    const [noParkingPreview, setNoParkingPreview] =
        useState<[number, number] | null>(null);

    // Selection system
    const [selectedParkingIds, setSelectedParkingIds] =
        useState<string[]>([]);

    const gpsDragRef = React.useRef<{
        index: number;
        original: GPSPoint[];
    } | null>(null);

    const [selectedYardObject, setSelectedYardObject] =
        useState<SelectableYardObject | null>(null);

    const [selectionBox, setSelectionBox] =
        useState<SelectionBox | null>(null);

    const selectionStartRef =
        useRef<[number, number] | null>(null);

    // Undo history
    const [undoStack, setUndoStack] =
        useState<UndoAction[]>([]);

    const [parkingStage, setParkingStage] =
        useState<0 | 1 | 2 | 3>(0);

    const [parkingHeading, setParkingHeading] =
        useState<number | null>(null);

    const [parkingSpacingPoint, setParkingSpacingPoint] =
        useState<[number, number] | null>(null);

    const [parkingRowPreview, setParkingRowPreview] =
        useState<[number, number] | null>(null);

    const imageLoaded = yardImage !== null;
    const setupComplete = setupStep === "complete";
    const wizardStarted =
        setupStep !== "load-image" && setupStep !== "ready";

    const [isRecalibratingGPS, setIsRecalibratingGPS] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionMessage, setSubmissionMessage] = useState("");
    const [submissionError, setSubmissionError] = useState("");

    const [isLoadingExistingYard, setIsLoadingExistingYard] =
        useState(false);

    const [existingYardNeedsValidation, setExistingYardNeedsValidation] =
        useState(false);

    const [loadedYardEtag, setLoadedYardEtag] =
        useState<string | null>(null);

    const [expectedYardImageSize, setExpectedYardImageSize] =
        useState<{ width: number; height: number } | null>(null);

    const canSave =
        imageLoaded &&
        !isLoadingExistingYard &&
        !existingYardNeedsValidation;

    const canSubmit =
        setupComplete &&
        !isSubmitting &&
        !isLoadingExistingYard &&
        !existingYardNeedsValidation;

    const canUseDrawingTools = setupComplete;

    const geofenceAreaPx =
        calculatePolygonAreaPx(geofencePoints);

    const geofenceAreaM2 =
        metersPerPx !== null
            ? geofenceAreaPx *
              metersPerPx *
              metersPerPx
            : null;

    const geofenceAreaFt2 =
        geofenceAreaM2 !== null
            ? geofenceAreaM2 * 10.7639104167
            : null;

    const geofenceAreaAcres =
        geofenceAreaFt2 !== null
            ? geofenceAreaFt2 / 43560
            : null;

    const snapParkingAngle45 = (
        anchor: [number, number],
        cursor: [number, number]
    ): number => {
        const dx = cursor[0] - anchor[0];
        const dy = cursor[1] - anchor[1];

        if (Math.abs(dx) < 0.000001 &&
            Math.abs(dy) < 0.000001) {
            return 0;
        }

        // Match main.py:
        // 0 degrees = up / north.
        const rawAngle =
            ((Math.atan2(dx, -dy) * 180) / Math.PI + 360) % 360;

        return (Math.round(rawAngle / 45) * 45) % 360;
    };

    const pointInsideGeofence = (
        point: [number, number]
    ): boolean => {
        if (geofencePoints.length < 3) {
            return true;
        }

        const [x, y] = point;
        let inside = false;

        for (
            let i = 0, j = geofencePoints.length - 1;
            i < geofencePoints.length;
            j = i++
        ) {
            const [xi, yi] = geofencePoints[i];
            const [xj, yj] = geofencePoints[j];

            const intersects =
                yi > y !== yj > y &&
                x <
                    ((xj - xi) * (y - yi)) /
                        (yj - yi + 1e-12) +
                        xi;

            if (intersects) {
                inside = !inside;
            }
        }

        return inside;
    };

    const snapParkingPoint45 = (
        start: [number, number],
        cursor: [number, number]
    ): [number, number] => {
        const dx = cursor[0] - start[0];
        const dy = cursor[1] - start[1];

        const distance = Math.hypot(dx, dy);

        if (distance < 0.000001) {
            return start;
        }

        const rawAngle =
            Math.atan2(dy, dx) * 180 / Math.PI;

        const snappedAngle =
            Math.round(rawAngle / 45) * 45;

        const rad =
            snappedAngle * Math.PI / 180;

        return [
            start[0] + Math.cos(rad) * distance,
            start[1] + Math.sin(rad) * distance,
        ];
    };

    const resetParkingRow = () => {
        setParkingStage(0);
        setParkingAnchor(null);
        setParkingPreviewAngle(null);
        setParkingHeading(null);
        setParkingSpacingPoint(null);
        setParkingRowPreview(null);
        setParkingError("");
    };

    const finishParkingRow = (
        ps1: [number, number],
        ps2: [number, number],
        rowEnd: [number, number],
        heading: number
    ) => {
        const spacing = Math.hypot(
            ps2[0] - ps1[0],
            ps2[1] - ps1[1]
        );

        const rowDx = rowEnd[0] - ps1[0];
        const rowDy = rowEnd[1] - ps1[1];

        const rowLength =
            Math.hypot(rowDx, rowDy);

        if (spacing <= 1) {
            setParkingError(
                "PS1 and PS2 are too close together."
            );
            return;
        }

        if (rowLength <= 1) {
            setParkingError(
                "Parking row endpoint is too close to PS1."
            );
            return;
        }

        const ux = rowDx / rowLength;
        const uy = rowDy / rowLength;

        const count =
            Math.floor(rowLength / spacing);

        const newSpaces: ParkingSpace[] = [];

        for (let i = 0; i <= count; i++) {
            const distance = spacing * i;

            if (distance > rowLength + 0.000001) {
                break;
            }

            const anchor: [number, number] = [
                ps1[0] + ux * distance,
                ps1[1] + uy * distance,
            ];

            const number =
                parkingSpaces.length +
                newSpaces.length +
                1;

            newSpaces.push({
                id: `PS${number}`,
                label: `PS${number}`,
                anchor,
                angle: heading,
                lengthFt: 53,
                widthFt: 9,
            });
        }

        setUndoStack((previous) => [
            ...previous,
            {
                type: "add-parking",
                spaces: newSpaces,
            },
        ]);

        setParkingSpaces(previous => [
            ...previous,
            ...newSpaces,
        ]);

        resetParkingRow();
    };

    const handleParkingCanvasClick = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            !setupComplete ||
            drawingTool !== "parking"
        ) {
            return;
        }

        const coords =
            getImageCoordinates(event);

        if (!coords) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        // CLICK 1 -------------------------------------------
        // Set PS1 anchor.
        if (parkingStage === 0) {
            if (!pointInsideGeofence(coords)) {
                setParkingError(
                    "Parking must start inside the geofence."
                );
                return;
            }

            setParkingAnchor(coords);
            setParkingPreviewAngle(0);
            setParkingHeading(null);
            setParkingSpacingPoint(null);
            setParkingRowPreview(null);
            setParkingStage(1);
            setParkingError("");

            return;
        }

        if (!parkingAnchor) {
            resetParkingRow();
            return;
        }

        // CLICK 2 -------------------------------------------
        // Lock parking-space heading.
        if (parkingStage === 1) {
            const heading =
                snapParkingAngle45(
                    parkingAnchor,
                    coords
                );

            setParkingHeading(heading);
            setParkingPreviewAngle(heading);
            setParkingStage(2);
            setParkingError("");

            return;
        }

        // CLICK 3 -------------------------------------------
        // Set PS1 -> PS2 spacing.
        if (parkingStage === 2) {
            const ps2 =
                snapParkingPoint45(
                    parkingAnchor,
                    coords
                );

            const spacing =
                Math.hypot(
                    ps2[0] - parkingAnchor[0],
                    ps2[1] - parkingAnchor[1]
                );

            if (spacing <= 1) {
                setParkingError(
                    "PS1 and PS2 are too close together."
                );
                return;
            }

            setParkingSpacingPoint(ps2);
            setParkingRowPreview(ps2);
            setParkingStage(3);
            setParkingError("");

            return;
        }

        // CLICK 4 -------------------------------------------
        // Set row endpoint and generate every parking space.
        if (
            parkingStage === 3 &&
            parkingSpacingPoint &&
            parkingHeading !== null
        ) {
            const rowEnd =
                snapParkingPoint45(
                    parkingAnchor,
                    coords
                );

            finishParkingRow(
                parkingAnchor,
                parkingSpacingPoint,
                rowEnd,
                parkingHeading
            );
        }
    };

    const updateParkingPreview = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            drawingTool !== "parking" ||
            !parkingAnchor
        ) {
            return;
        }

        const coords =
            getImageCoordinates(event);

        if (!coords) {
            return;
        }

        // Aim parking-space heading.
        if (parkingStage === 1) {
            setParkingPreviewAngle(
                snapParkingAngle45(
                    parkingAnchor,
                    coords
                )
            );
            return;
        }

        // PS1 -> PS2 spacing.
        if (parkingStage === 2) {
            setParkingRowPreview(
                snapParkingPoint45(
                    parkingAnchor,
                    coords
                )
            );
            return;
        }

        // Row endpoint.
        if (parkingStage === 3) {
            setParkingRowPreview(
                snapParkingPoint45(
                    parkingAnchor,
                    coords
                )
            );
        }
    };

    const clearSelection = () => {
        setSelectedParkingIds([]);
        setSelectedYardObject(null);
        setSelectionBox(null);
        selectionStartRef.current = null;
    };

    const parkingAnchorInsideBox = (
        space: ParkingSpace,
        box: SelectionBox
    ): boolean => {
        const minX = Math.min(
            box.start[0],
            box.end[0]
        );

        const maxX = Math.max(
            box.start[0],
            box.end[0]
        );

        const minY = Math.min(
            box.start[1],
            box.end[1]
        );

        const maxY = Math.max(
            box.start[1],
            box.end[1]
        );

        return (
            space.anchor[0] >= minX &&
            space.anchor[0] <= maxX &&
            space.anchor[1] >= minY &&
            space.anchor[1] <= maxY
        );
    };

    const handleSelectMouseDown = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (gpsDragRef.current) return;

        if (
            !setupComplete ||
            drawingTool !== "select"
        ) {
            return;
        }

        // Left mouse button only.
        if (event.button !== 0) {
            return;
        }

        const coords =
            getImageCoordinates(event);

        if (!coords) {
            return;
        }

        selectionStartRef.current = coords;

        setSelectionBox({
            start: coords,
            end: coords,
        });

        // Normal drag starts a new selection.
        // Shift-drag can be added later for additive selection.
        setSelectedParkingIds([]);
        setSelectedYardObject(null);

        event.preventDefault();
    };

    const handleSelectMouseMove = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (gpsDragRef.current) return;

        if (
            drawingTool !== "select" ||
            !selectionStartRef.current
        ) {
            return;
        }

        const coords =
            getImageCoordinates(event);

        if (!coords) {
            return;
        }

        const box: SelectionBox = {
            start: selectionStartRef.current,
            end: coords,
        };

        setSelectionBox(box);

        const ids = parkingSpaces
            .filter((space) =>
                parkingAnchorInsideBox(
                    space,
                    box
                )
            )
            .map((space) => space.id);

        setSelectedParkingIds(ids);
    };

    const pointInsideYardPolygon = (
        point: [number, number],
        polygon: [number, number][]
    ): boolean => {
        if (polygon.length < 3) return false;

        const [px, py] = point;
        let inside = false;

        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const [xi, yi] = polygon[i];
            const [xj, yj] = polygon[j];

            if (
                (yi > py) !== (yj > py) &&
                px < ((xj - xi) * (py - yi)) / (yj - yi) + xi
            ) {
                inside = !inside;
            }
        }

        return inside;
    };

    const handleSelectMouseUp = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (gpsDragRef.current) return;

        if (
            drawingTool !== "select" ||
            !selectionStartRef.current
        ) {
            return;
        }

        const start =
            selectionStartRef.current;

        const end =
            getImageCoordinates(event) ??
            start;

        const distance =
            Math.hypot(
                end[0] - start[0],
                end[1] - start[1]
            );

        /*
         * A very short drag is treated as a click.
         * Find the nearest parking anchor.
         */
        if (distance < 5) {
            // GPS calibration points take priority when clicked directly.
            const gpsHitIndex = gpsPoints.findIndex(
                (point) =>
                    point !== null &&
                    Math.hypot(point.x - end[0], point.y - end[1]) <= 12
            );

            if (gpsHitIndex !== -1) {
                setSelectedYardObject({
                    type: "gps-point",
                    id: String(gpsHitIndex),
                });
                setSelectedParkingIds([]);
                selectionStartRef.current = null;
                setSelectionBox(null);
                return;
            }

            // Detect individual geofence vertices.
            const geofenceHitIndex = geofencePoints.findIndex(
                ([x, y]) =>
                    Math.hypot(x - end[0], y - end[1]) <= 12
            );

            if (geofenceHitIndex !== -1) {
                setSelectedYardObject({
                    type: "geofence-vertex",
                    id: String(geofenceHitIndex),
                });
                setSelectedParkingIds([]);
                selectionStartRef.current = null;
                setSelectionBox(null);
                return;
            }

            // Select scanners by clicking near their center.
            const scannerHit = [...scanners].reverse().find(
                (scanner) =>
                    Math.hypot(
                        scanner.px[0] - end[0],
                        scanner.px[1] - end[1]
                    ) <= 24
            );

            if (scannerHit) {
                setSelectedYardObject({
                    type: "scanner",
                    id: scannerHit.id,
                });
                setSelectedParkingIds([]);
                selectionStartRef.current = null;
                setSelectionBox(null);
                return;
            }

            // Select the POD by clicking near its marker.
            if (
                pod &&
                Math.hypot(
                    pod.px[0] - end[0],
                    pod.px[1] - end[1]
                ) <= 18
            ) {
                setSelectedYardObject({
                    type: "pod",
                    id: "POD_HOME",
                });
                setSelectedParkingIds([]);
                selectionStartRef.current = null;
                setSelectionBox(null);
                return;
            }

            let nearest:
                | ParkingSpace
                | null = null;

            let nearestDistance =
                Number.POSITIVE_INFINITY;

            for (const space of parkingSpaces) {
                const d =
                    Math.hypot(
                        space.anchor[0] - end[0],
                        space.anchor[1] - end[1]
                    );

                if (d < nearestDistance) {
                    nearestDistance = d;
                    nearest = space;
                }
            }

            /*
             * Selection tolerance is scaled to the
             * calibrated yard when possible.
             */
            const tolerance =
                metersPerPx !== null
                    ? 12 * (0.3048 / metersPerPx)
                    : 30;

            if (
                nearest &&
                nearestDistance <= tolerance
            ) {
                setSelectedYardObject(null);
                setSelectedParkingIds([
                    nearest.id,
                ]);
                selectionStartRef.current = null;
                setSelectionBox(null);
                return;
            }
            // Detect smaller yard objects before large area polygons.
            const crosswalkHit = [...crosswalks].reverse().find((item) => {
                const geometry = getCrosswalkGeometry(item.p1, item.p2, item.widthFt);
                return geometry !== null &&
                    pointInsideYardPolygon(end, geometry.band);
            });

            const noParkingHit = [...noParkingZones].reverse().find(
                (item) => pointInsideYardPolygon(end, item.points)
            );

            const overflowHit = [...overflowBlocks].reverse().find(
                (item) => pointInsideYardPolygon(end, item.points)
            );

            const objectHit = crosswalkHit
                ? { type: "crosswalk" as const, id: crosswalkHit.id }
                : noParkingHit
                ? { type: "no-parking" as const, id: noParkingHit.id }
                : overflowHit
                ? { type: "overflow" as const, id: overflowHit.id }
                : null;

            if (objectHit) {
                setSelectedYardObject(objectHit);
                setSelectedParkingIds([]);
                selectionStartRef.current = null;
                setSelectionBox(null);
                return;
            }

            // Select a building by clicking inside its boundary.
            const buildingHit = [...buildings].reverse().find(
                (building) => pointInsideYardPolygon(end, building.points)
            );

            if (buildingHit) {
                setSelectedYardObject({
                    type: "building",
                    id: buildingHit.id,
                });
                setSelectedParkingIds([]);
                selectionStartRef.current = null;
                setSelectionBox(null);
                return;
            }

            // Select a section by clicking inside its rectangle.
            const sectionHit = [...sections].reverse().find((section) => {
                const { x, y, width, height } = section.rect;
                const minX = Math.min(x, x + width);
                const maxX = Math.max(x, x + width);
                const minY = Math.min(y, y + height);
                const maxY = Math.max(y, y + height);

                return (
                    end[0] >= minX &&
                    end[0] <= maxX &&
                    end[1] >= minY &&
                    end[1] <= maxY
                );
            });

            if (sectionHit) {
                setSelectedYardObject({
                    type: "section",
                    id: sectionHit.id,
                });
                setSelectedParkingIds([]);
                selectionStartRef.current = null;
                setSelectionBox(null);
                return;
            }


        }

        selectionStartRef.current = null;
        setSelectionBox(null);
    };

    const deleteSelectedYardObject = () => {
        if (!selectedYardObject) return;

        const { type, id } = selectedYardObject;
        const index = Number(id);

        const exists =
            type === "gps-point" ? Number.isInteger(index) && !!gpsPoints[index] :
            type === "geofence-vertex" ? Number.isInteger(index) && !!geofencePoints[index] :
            type === "section" ? sections.some(x => x.id === id) :
            type === "building" ? buildings.some(x => x.id === id) :
            type === "pod" ? pod !== null :
            type === "scanner" ? scanners.some(x => x.id === id) :
            type === "crosswalk" ? crosswalks.some(x => x.id === id) :
            type === "no-parking" ? noParkingZones.some(x => x.id === id) :
            type === "overflow" ? overflowBlocks.some(x => x.id === id) :
            false;

        if (!exists) {
            clearSelection();
            return;
        }

        if (type === "gps-point" || type === "geofence-vertex") {
            if (!window.confirm(
                "Delete this flight-safety calibration point? " +
                "GPS/geofence validation must be completed again before submission."
            )) return;
        }

        setUndoStack(previous => [
            ...previous,
            {
                type: "delete-yard-object",
                snapshot: {
                    gpsPoints: [...gpsPoints],
                    gpsInputs: [...gpsInputs],
                    geoCalibration,
                    geofencePoints: [...geofencePoints],
                    geofenceComplete,
                    sections: [...sections],
                    buildings: [...buildings],
                    pod,
                    scanners: [...scanners],
                    crosswalks: [...crosswalks],
                    noParkingZones: [...noParkingZones],
                    overflowBlocks: [...overflowBlocks],
                },
            },
        ]);

        switch (type) {
            case "gps-point":
                setGpsPoints(previous =>
                    previous.map((point, i) => i === index ? null : point)
                );
                setGpsInputs(previous =>
                    previous.map((point, i) =>
                        i === index ? { lat: "", lon: "" } : point
                    )
                );
                setGeoCalibration(null);
                break;
            case "geofence-vertex":
                setGeofencePoints(previous =>
                    previous.filter((_, i) => i !== index)
                );
                setGeofenceComplete(false);
                break;
            case "section":
                setSections(previous => previous.filter(x => x.id !== id));
                break;
            case "building":
                setBuildings(previous => previous.filter(x => x.id !== id));
                break;
            case "pod":
                setPod(null);
                break;
            case "scanner":
                setScanners(previous => previous.filter(x => x.id !== id));
                break;
            case "crosswalk":
                setCrosswalks(previous => previous.filter(x => x.id !== id));
                break;
            case "no-parking":
                setNoParkingZones(previous => previous.filter(x => x.id !== id));
                break;
            case "overflow":
                setOverflowBlocks(previous => previous.filter(x => x.id !== id));
                break;
        }

        clearSelection();
    };

    const deleteSelectedItems = () => {
        if (selectedYardObject) {
            deleteSelectedYardObject();
            return;
        }
        if (
            selectedParkingIds.length === 0
        ) {
            return;
        }

        const deleted =
            parkingSpaces.filter((space) =>
                selectedParkingIds.includes(
                    space.id
                )
            );

        if (deleted.length === 0) {
            clearSelection();
            return;
        }

        setUndoStack((previous) => [
            ...previous,
            {
                type: "delete-parking",
                spaces: deleted,
            },
        ]);

        setParkingSpaces((previous) =>
            previous.filter(
                (space) =>
                    !selectedParkingIds.includes(
                        space.id
                    )
            )
        );

        clearSelection();
    };

    const undoLastAction = () => {
        if (undoStack.length === 0) {
            return;
        }

        const action =
            undoStack[
                undoStack.length - 1
            ];

        if (action.type === "move-gps-point") {
            setUndoStack(previous => previous.slice(0, -1));
            setGpsPoints(action.points);
            setGeoCalibration(action.calibration);
            return;
        }

        if (action.type === "delete-yard-object") {
            const snapshot = action.snapshot;
            setGpsPoints(snapshot.gpsPoints);
            setGpsInputs(snapshot.gpsInputs);
            setGeoCalibration(snapshot.geoCalibration);
            setGeofencePoints(snapshot.geofencePoints);
            setGeofenceComplete(snapshot.geofenceComplete);
            setSections(snapshot.sections);
            setBuildings(snapshot.buildings);
            setPod(snapshot.pod);
            setScanners(snapshot.scanners);
            setCrosswalks(snapshot.crosswalks);
            setNoParkingZones(snapshot.noParkingZones);
            setOverflowBlocks(snapshot.overflowBlocks);
            clearSelection();
        }

        if (
            action.type ===
            "delete-parking"
        ) {
            setParkingSpaces((previous) => {
                const existing =
                    new Set(
                        previous.map(
                            (space) => space.id
                        )
                    );

                return [
                    ...previous,
                    ...action.spaces.filter(
                        (space) =>
                            !existing.has(
                                space.id
                            )
                    ),
                ];
            });
        }

        if (
            action.type ===
            "add-parking"
        ) {
            const ids =
                new Set(
                    action.spaces.map(
                        (space) => space.id
                    )
                );

            setParkingSpaces((previous) =>
                previous.filter(
                    (space) =>
                        !ids.has(space.id)
                )
            );

            clearSelection();
        }

        if (
            action.type ===
            "move-parking"
        ) {
            const previousPositions =
                new Map(
                    action.spaces.map(
                        (space) => [
                            space.id,
                            space.anchor,
                        ]
                    )
                );

            setParkingSpaces((previous) =>
                previous.map((space) => {
                    const oldAnchor =
                        previousPositions.get(
                            space.id
                        );

                    if (!oldAnchor) {
                        return space;
                    }

                    return {
                        ...space,
                        anchor: oldAnchor,
                    };
                })
            );
        }

        if (action.type === "add-section") {
            setSections((previous) =>
                previous.filter(
                    (section) =>
                        section.id !== action.section.id
                )
            );
        }

        if (action.type === "add-no-parking") {
            setNoParkingZones((previous) =>
                previous.filter(
                    (zone) =>
                        zone.id !== action.zone.id
                )
            );
        }

        if (
            action.type === "add-building"
        ) {
            setBuildings((previous) =>
                previous.filter(
                    (building) =>
                        building.id !==
                        action.building.id
                )
            );
        }

        if (
            action.type === "add-crosswalk"
        ) {
            setCrosswalks((previous) =>
                previous.filter(
                    (crosswalk) =>
                        crosswalk.id !==
                        action.crosswalk.id
                )
            );
        }

        setUndoStack((previous) =>
            previous.slice(0, -1)
        );
    };

    const handleLoadImage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (!file) return;

        if (!["image/png", "image/jpeg"].includes(file.type)) {
            setSubmissionError("Please select a PNG or JPEG image.");
            event.target.value = "";
            return;
        }

        const imageUrl = URL.createObjectURL(file);
        const testImage = new Image();

        testImage.onload = () => {
            const width = testImage.naturalWidth;
            const height = testImage.naturalHeight;

            if (
                expectedYardImageSize &&
                (
                    width !== expectedYardImageSize.width ||
                    height !== expectedYardImageSize.height
                )
            ) {
                URL.revokeObjectURL(imageUrl);

                setSubmissionError(
                    `Image dimensions do not match the saved yard. ` +
                    `Expected ${expectedYardImageSize.width} x ` +
                    `${expectedYardImageSize.height}, ` +
                    `received ${width} x ${height}.`
                );

                event.target.value = "";
                return;
            }

            setYardImage((previous) => {
                if (previous?.startsWith("blob:")) {
                    URL.revokeObjectURL(previous);
                }
                return imageUrl;
            });

            setYardImageName(file.name);
            setSubmissionError("");

            if (expectedYardImageSize) {
                setSubmissionMessage(
                    "Yard image dimensions verified. " +
                    "Existing yard geometry is preserved. " +
                    "Submission remains locked pending final validation."
                );
            } else if (!existingYardNeedsValidation) {
                setSetupStep("ready");
            }

            event.target.value = "";
        };

        testImage.onerror = () => {
            URL.revokeObjectURL(imageUrl);
            setSubmissionError("Unable to read the selected image.");
            event.target.value = "";
        };

        testImage.src = imageUrl;
    };

    const startSetupWizard = () => {
        if (!imageLoaded) {
            return;
        }

        setYardInfoError("");
        setSetupStep("yard-info");
    };

    const getGpsIndex = (): number => {
        switch (setupStep) {
            case "gps-p1": return 0;
            case "gps-p2": return 1;
            case "gps-p3": return 2;
            case "gps-p4": return 3;
            default: return -1;
        }
    };

    const loadSampleCoordinates = () => {
        setGpsInputs([
            { lat: "33.448500", lon: "-112.074500" },
            { lat: "33.448500", lon: "-112.073500" },
            { lat: "33.447700", lon: "-112.073500" },
            { lat: "33.447700", lon: "-112.074500" },
        ]);

        setGpsError("");
    };

    const updateGpsInput = (
        index: number,
        field: "lat" | "lon",
        value: string
    ) => {
        setGpsInputs((previous) =>
            previous.map((entry, i) =>
                i === index
                    ? { ...entry, [field]: value }
                    : entry
            )
        );

        setGpsError("");
    };

    const resetGpsPoint = (index: number) => {
        setGpsPoints((previous) =>
            previous.map((point, i) =>
                i === index ? null : point
            )
        );

        setGpsError("");
        setGeoCalibration(null);

        setSetupStep(
            ["gps-p1", "gps-p2", "gps-p3", "gps-p4"][index] as SetupStep
        );
    };

    const handleCanvasWheel = (
        event: React.WheelEvent<HTMLDivElement>
    ) => {
        if (
            !yardImage ||
            !imageRef.current
        ) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const imageRect =
            imageRef.current.getBoundingClientRect();

        const currentZoom = zoom;
        const factor = event.deltaY < 0 ? 1.1 : 1 / 1.1;
        const nextZoom = Math.min(
            6,
            Math.max(1, currentZoom * factor)
        );

        if (nextZoom === currentZoom) return;

        const ratio = nextZoom / currentZoom;

        // The image uses a center transform origin.
        const centerX = imageRect.left + imageRect.width / 2;
        const centerY = imageRect.top + imageRect.height / 2;

        // Keep the image point under the mouse stationary.
        setZoomOffset((previous) => ({
            x: previous.x + (event.clientX - centerX) * (1 - ratio),
            y: previous.y + (event.clientY - centerY) * (1 - ratio),
        }));

        setZoom(nextZoom);
    };

    const getImageCoordinates = (
        event: React.MouseEvent<HTMLDivElement>
    ): [number, number] | null => {
        if (!imageRef.current) {
            return null;
        }

        const imageRect = imageRef.current.getBoundingClientRect();

        if (
            event.clientX < imageRect.left ||
            event.clientX > imageRect.right ||
            event.clientY < imageRect.top ||
            event.clientY > imageRect.bottom
        ) {
            return null;
        }

        const x =
            ((event.clientX - imageRect.left) / imageRect.width) *
            imageRef.current.naturalWidth;

        const y =
            ((event.clientY - imageRect.top) / imageRect.height) *
            imageRef.current.naturalHeight;

        return [x, y];
    };

    const continueFromYardInfo = () => {
        const altitudeA = Number(yardInfo.altitudeA);
        const altitudeB = Number(yardInfo.altitudeB);
        const altitudeC = Number(yardInfo.altitudeC);

        if (!yardInfo.name.trim()) {
            setYardInfoError("Enter the yard name.");
            return;
        }

        if (!yardInfo.address.trim()) {
            setYardInfoError("Enter the yard address.");
            return;
        }

        if (!/^\d{5}$/.test(yardInfo.zipCode.trim())) {
            setYardInfoError("Enter a valid 5-digit ZIP code.");
            return;
        }

        if (
            !Number.isFinite(altitudeA) ||
            altitudeA < 15 ||
            altitudeA > 500
        ) {
            setYardInfoError(
                "Transit Height must be between 15 and 500 ft."
            );
            return;
        }

        if (
            !Number.isFinite(altitudeB) ||
            altitudeB < 15 ||
            altitudeB > 500
        ) {
            setYardInfoError(
                "Max Height must be between 15 and 500 ft."
            );
            return;
        }

        if (
            !Number.isFinite(altitudeC) ||
            altitudeC < 2 ||
            altitudeC > 15
        ) {
            setYardInfoError(
                "Min Height must be between 2 and 15 ft."
            );
            return;
        }

        setYardInfoError("");
        setSetupStep("gps-p1");
    };

    const finishGeofence = () => {
        if (setupStep !== "geofence") {
            return;
        }

        if (geofencePoints.length < 3) {
            setGeofenceError(
                "At least 3 geofence points are required."
            );
            return;
        }

        setGeofenceComplete(true);
        setGeofenceError("");
        setSetupStep("scale");
    };

    const resetGeofence = () => {
        setGeofencePoints([]);
        setGeofenceComplete(false);
        setGeofenceError("");
        setSetupStep("geofence");
    };

    const confirmScaleCalibration = () => {
        if (
            scalePxLength === null ||
            !scaleDragStart ||
            !scaleDragEnd
        ) {
            setScaleError(
                "Drag a measurement line along the trailer first."
            );
            return;
        }

        const feet = Number(scaleFeet);

        if (!Number.isFinite(feet) || feet <= 0) {
            setScaleError(
                "Enter a positive trailer length in feet."
            );
            return;
        }

        const calculatedMetersPerPx =
            (feet * 0.3048) / scalePxLength;

        setMetersPerPx(calculatedMetersPerPx);
        setScaleError("");
        setSetupStep("complete");
    };

    const resetScaleCalibration = () => {
        scaleDragStartRef.current = null;
        setScaleDragStart(null);
        setScaleDragEnd(null);
        setScalePxLength(null);
        setMetersPerPx(null);
        setScaleError("");
    };

    const handleGPSDragMove = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        const drag = gpsDragRef.current;
        if (!drag) return;
        const coords = getImageCoordinates(event);
        if (!coords) return;

        // If the mouse button was released outside the canvas,
        // cancel the drag and restore the original calibration.
        if ((event.buttons & 1) === 0) {
            gpsDragRef.current = null;
            setGpsPoints(drag.original);
            setGpsError("GPS drag canceled: release inside the yard image.");
            return;
        }

        setGpsPoints(previous => previous.map((point, index) =>
            index === drag.index && point
                ? { ...point, x: coords[0], y: coords[1] }
                : point
        ));
    };

    const handleGPSDragEnd = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        const drag = gpsDragRef.current;
        if (!drag) return;
        gpsDragRef.current = null;
        const coords = getImageCoordinates(event);
        if (!coords) {
            setGpsPoints(drag.original);
            setGpsError("GPS drag canceled: release inside the yard image.");
            return;
        }
        const updated = drag.original.map((point, index) =>
            index === drag.index
                ? { ...point, x: coords[0], y: coords[1] }
                : point
        );
        try {
            const calibration = computeGeoCalibration(updated);
            setUndoStack(previous => [
                ...previous,
                {
                    type: "move-gps-point",
                    points: drag.original,
                    calibration: geoCalibration,
                },
            ]);
            setGpsPoints(updated);
            setGeoCalibration(calibration);
            setGpsError("");
        } catch (error) {
            setGpsPoints(drag.original);
            setGpsError(error instanceof Error ? error.message : "Invalid GPS calibration.");
        }
    };

    const handleScaleMouseDown = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (setupStep !== "scale") {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        // Store immediately so mousemove/mouseup do not have to wait
        // for React state to update.
        scaleDragStartRef.current = coords;

        setScaleDragStart(coords);
        setScaleDragEnd(coords);
        setScalePxLength(null);
        setMetersPerPx(null);
        setScaleError("");
    };

    const handleScaleMouseUp = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (setupStep !== "scale") {
            return;
        }

        const startPoint = scaleDragStartRef.current;

        if (!startPoint) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            scaleDragStartRef.current = null;
            setScaleError(
                "Release the measurement inside the yard image."
            );
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const snappedEnd = snapEndpointDegrees(
            startPoint,
            coords,
            45
        );

        const dx = snappedEnd[0] - startPoint[0];
        const dy = snappedEnd[1] - startPoint[1];
        const pxLength = Math.hypot(dx, dy);

        scaleDragStartRef.current = null;

        if (pxLength < 5) {
            setScaleDragStart(null);
            setScaleDragEnd(null);
            setScalePxLength(null);
            setScaleError(
                "Drag along the full length of the trailer."
            );
            return;
        }

        setScaleDragStart(startPoint);
        setScaleDragEnd(snappedEnd);
        setScalePxLength(pxLength);
        setMetersPerPx(null);
        setScaleError("");
    };

    const handleSectionMouseDown = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            !setupComplete ||
            drawingTool !== "section" ||
            event.button !== 0
        ) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        sectionDragStartRef.current = coords;
        setSectionDragStart(coords);
        setSectionDragEnd(coords);
    };

    const handleSectionMouseMove = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            drawingTool !== "section" ||
            !sectionDragStartRef.current
        ) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            return;
        }

        setSectionDragEnd(coords);
    };

    const handleSectionMouseUp = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            drawingTool !== "section" ||
            !sectionDragStartRef.current
        ) {
            return;
        }

        const start = sectionDragStartRef.current;
        const end = getImageCoordinates(event);

        sectionDragStartRef.current = null;

        if (!end) {
            setSectionDragStart(null);
            setSectionDragEnd(null);
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const x = Math.min(start[0], end[0]);
        const y = Math.min(start[1], end[1]);
        const width = Math.abs(end[0] - start[0]);
        const height = Math.abs(end[1] - start[1]);

        setSectionDragStart(null);
        setSectionDragEnd(null);

        // Ignore accidental clicks/tiny drags.
        if (width < 5 || height < 5) {
            return;
        }

        const nextNumber =
            sections.reduce((largest, section) => {
                const match = section.id.match(/^S(\d+)$/);

                if (!match) {
                    return largest;
                }

                return Math.max(
                    largest,
                    Number(match[1])
                );
            }, 0) + 1;

        const id = `S${nextNumber}`;

        const section: YardSection = {
            id,
            label: id,
            rect: {
                x,
                y,
                width,
                height,
            },
            entry: [
                x + width / 2,
                y + height,
            ],
            orientationDeg: 0,
        };

        setSections((previous) => [
            ...previous,
            section,
        ]);

        setUndoStack((previous) => [
            ...previous,
            {
                type: "add-section",
                section,
            },
        ]);
    };

    // =====================================================
    // Buildings
    // Desktop behavior:
    // - polygon vertices
    // - each new edge snaps to 15 degrees
    // - double-click or Enter finishes
    // =====================================================

    const handleBuildingCanvasClick = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            !setupComplete ||
            drawingTool !== "building"
        ) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            return;
        }

        const point =
            buildingPoints.length > 0
                ? snapPolygonPoint15(
                      buildingPoints,
                      coords
                  )
                : coords;

        setBuildingPoints((previous) => [
            ...previous,
            point,
        ]);
    };

    const updateBuildingPreview = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            drawingTool !== "building" ||
            buildingPoints.length === 0
        ) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            setBuildingPreview(null);
            return;
        }

        setBuildingPreview(
            snapPolygonPoint15(
                buildingPoints,
                coords
            )
        );
    };

    const finishBuilding = () => {
        if (
            drawingTool !== "building" ||
            buildingPoints.length < 3
        ) {
            return;
        }

        const nextNumber =
            buildings.reduce((largest, building) => {
                const match =
                    building.id.match(/^BLDG(\\d+)$/);

                if (!match) {
                    return largest;
                }

                return Math.max(
                    largest,
                    Number(match[1])
                );
            }, 0) + 1;

        const defaultName =
            nextNumber <= 26
                ? `Building ${String.fromCharCode(
                      64 + nextNumber
                  )}`
                : `Building ${nextNumber}`;

        const enteredName =
            window.prompt(
                "Enter building name:",
                defaultName
            );

        const label =
            enteredName?.trim() ||
            defaultName;

        const building: YardBuilding = {
            id: `BLDG${nextNumber}`,
            label,
            points: [...buildingPoints],
        };

        setBuildings((previous) => [
            ...previous,
            building,
        ]);

        setUndoStack((previous) => [
            ...previous,
            {
                type: "add-building",
                building,
            },
        ]);

        setBuildingPoints([]);
        setBuildingPreview(null);
    };

    // =====================================================
    // Crosswalks
    // Desktop behavior:
    // click start -> preview -> click end
    // fixed 12-ft width
    // =====================================================

    const handleCrosswalkCanvasClick = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            !setupComplete ||
            drawingTool !== "crosswalk"
        ) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            return;
        }

        if (!crosswalkStart) {
            setCrosswalkStart(coords);
            setCrosswalkPreview(coords);
            return;
        }

        const dx = coords[0] - crosswalkStart[0];
        const dy = coords[1] - crosswalkStart[1];

        if (Math.hypot(dx, dy) < 5) {
            setCrosswalkStart(null);
            setCrosswalkPreview(null);
            return;
        }

        const nextNumber =
            crosswalks.reduce(
                (largest, crosswalk) => {
                    const match =
                        crosswalk.id.match(/^CW(\\d+)$/);

                    if (!match) {
                        return largest;
                    }

                    return Math.max(
                        largest,
                        Number(match[1])
                    );
                },
                0
            ) + 1;

        const crosswalk: Crosswalk = {
            id: `CW${nextNumber}`,
            p1: crosswalkStart,
            p2: coords,
            widthFt: 12,
        };

        setCrosswalks((previous) => [
            ...previous,
            crosswalk,
        ]);

        setUndoStack((previous) => [
            ...previous,
            {
                type: "add-crosswalk",
                crosswalk,
            },
        ]);

        setCrosswalkStart(null);
        setCrosswalkPreview(null);
    };

    const updateCrosswalkPreview = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            drawingTool !== "crosswalk" ||
            !crosswalkStart
        ) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (coords) {
            setCrosswalkPreview(coords);
        }
    };

    const getCrosswalkGeometry = (
        p1: [number, number],
        p2: [number, number],
        widthFt: number
    ) => {
        const dx = p2[0] - p1[0];
        const dy = p2[1] - p1[1];
        const length = Math.hypot(dx, dy);

        if (
            length < 0.000001 ||
            !metersPerPx ||
            metersPerPx <= 0
        ) {
            return null;
        }

        const ux = dx / length;
        const uy = dy / length;

        const nx = -uy;
        const ny = ux;

        const pxPerFt =
            0.3048 / metersPerPx;

        const widthPx =
            widthFt * pxPerFt;

        const half =
            widthPx / 2;

        const band: [number, number][] = [
            [
                p1[0] + nx * half,
                p1[1] + ny * half,
            ],
            [
                p2[0] + nx * half,
                p2[1] + ny * half,
            ],
            [
                p2[0] - nx * half,
                p2[1] - ny * half,
            ],
            [
                p1[0] - nx * half,
                p1[1] - ny * half,
            ],
        ];

        const stripeWidth =
            Math.max(
                6,
                widthPx * 0.18
            );

        const step =
            stripeWidth * 2;

        const stripes:
            [number, number][][] = [];

        let distance = stripeWidth;

        const halfStripe =
            stripeWidth / 2;

        while (
            distance <
            length - halfStripe
        ) {
            const cx =
                p1[0] + ux * distance;

            const cy =
                p1[1] + uy * distance;

            stripes.push([
                [
                    cx +
                        ux * halfStripe +
                        nx * half,
                    cy +
                        uy * halfStripe +
                        ny * half,
                ],
                [
                    cx +
                        ux * halfStripe -
                        nx * half,
                    cy +
                        uy * halfStripe -
                        ny * half,
                ],
                [
                    cx -
                        ux * halfStripe -
                        nx * half,
                    cy -
                        uy * halfStripe -
                        ny * half,
                ],
                [
                    cx -
                        ux * halfStripe +
                        nx * half,
                    cy -
                        uy * halfStripe +
                        ny * half,
                ],
            ]);

            distance += step;
        }

        return {
            band,
            stripes,
        };
    };

    const handleNoParkingCanvasClick = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            !setupComplete ||
            drawingTool !== "no-parking"
        ) {
            return;
        }

        // Ignore the second click generated by a double-click.
        if (event.detail > 1) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        setNoParkingPoints((previous) => {
            const snapped =
                snapPolygonPoint15(
                    previous,
                    coords
                );

            return [
                ...previous,
                snapped,
            ];
        });

        setNoParkingPreview(null);
    };

    const updateNoParkingPreview = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            drawingTool !== "no-parking" ||
            noParkingPoints.length === 0
        ) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            return;
        }

        const snapped =
            snapPolygonPoint15(
                noParkingPoints,
                coords
            );

        setNoParkingPreview(snapped);
    };

    const finishNoParking = () => {
        if (
            drawingTool !== "no-parking" ||
            noParkingPoints.length < 3
        ) {
            return;
        }

        const nextNumber =
            noParkingZones.reduce((largest, zone) => {
                const match = zone.id.match(/^NP(\d+)$/);

                if (!match) {
                    return largest;
                }

                return Math.max(
                    largest,
                    Number(match[1])
                );
            }, 0) + 1;

        const zone: NoParkingZone = {
            id: `NP${nextNumber}`,
            points: noParkingPoints.map(
                ([x, y]) => [x, y]
            ),
        };

        setNoParkingZones((previous) => [
            ...previous,
            zone,
        ]);

        setUndoStack((previous) => [
            ...previous,
            {
                type: "add-no-parking",
                zone,
            },
        ]);

        setNoParkingPoints([]);
        setNoParkingPreview(null);
    };

    const pointInsidePolygon = (
        point: [number, number],
        polygon: [number, number][]
    ) => {
        if (polygon.length < 3) {
            return true;
        }

        const [x, y] = point;
        let inside = false;

        for (
            let i = 0, j = polygon.length - 1;
            i < polygon.length;
            j = i++
        ) {
            const [xi, yi] = polygon[i];
            const [xj, yj] = polygon[j];

            const intersects =
                yi > y !== yj > y &&
                x <
                    ((xj - xi) * (y - yi)) /
                        (yj - yi || Number.EPSILON) +
                        xi;

            if (intersects) {
                inside = !inside;
            }
        }

        return inside;
    };

    const handleOverflowCanvasClick = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            !setupComplete ||
            drawingTool !== "overflow"
        ) {
            return;
        }

        if (event.detail > 1) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            return;
        }

        const snapped =
            snapPolygonPoint15(
                overflowPoints,
                coords
            );

        setOverflowPoints((previous) => [
            ...previous,
            snapped,
        ]);

        setOverflowPreview(snapped);
    };

    const updateOverflowPreview = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            drawingTool !== "overflow" ||
            overflowPoints.length === 0
        ) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            return;
        }

        setOverflowPreview(
            snapPolygonPoint15(
                overflowPoints,
                coords
            )
        );
    };

    const finishOverflow = () => {
        if (
            drawingTool !== "overflow" ||
            overflowPoints.length < 3
        ) {
            return;
        }

        const points =
            overflowPoints.map(
                ([x, y]) =>
                    [x, y] as [number, number]
            );

        const outsideGeofence =
            geofenceComplete &&
            geofencePoints.length >= 3 &&
            points.some(
                (point) =>
                    !pointInsidePolygon(
                        point,
                        geofencePoints
                    )
            );

        let active = true;

        if (outsideGeofence) {
            const keepInactive =
                window.confirm(
                    "This overflow extends beyond the geofence.\n\n" +
                    "Press OK to keep the overflow inactive.\n" +
                    "Press Cancel to discard it."
                );

            if (!keepInactive) {
                setOverflowPoints([]);
                setOverflowPreview(null);
                return;
            }

            active = false;
        }

        const nextNumber =
            overflowBlocks.reduce(
                (largest, block) => {
                    const match =
                        block.id.match(/^B(\d+)$/);

                    if (!match) {
                        return largest;
                    }

                    return Math.max(
                        largest,
                        Number(match[1])
                    );
                },
                0
            ) + 1;

        const block: OverflowBlock = {
            id: `B${nextNumber}`,
            label: `Overflow ${nextNumber}`,
            points,
            orientationDeg: 0,
            active,
        };

        setOverflowBlocks((previous) => [
            ...previous,
            block,
        ]);

        setOverflowPoints([]);
        setOverflowPreview(null);
    };

    const scannerHeading45 = (
        anchor: [number, number],
        cursor: [number, number]
    ) => {
        const dx = cursor[0] - anchor[0];
        const dy = cursor[1] - anchor[1];

        if (
            Math.abs(dx) < 1e-9 &&
            Math.abs(dy) < 1e-9
        ) {
            return 0;
        }

        const raw =
            (
                Math.atan2(dx, -dy) *
                180 /
                Math.PI +
                360
            ) % 360;

        return (
            Math.round(raw / 45) * 45
        ) % 360;
    };

    const scannerRotatedPoint = (
        x: number,
        y: number,
        ox: number,
        oy: number,
        angle: number
    ): [number, number] => {
        const rad = angle * Math.PI / 180;
        const ca = Math.cos(rad);
        const sa = Math.sin(rad);

        return [
            x + ox * ca - oy * sa,
            y + ox * sa + oy * ca,
        ];
    };

    const scannerSquarePoints = (
        center: [number, number],
        centerOffsetY: number,
        angle: number
    ) => {
        const [x, y] = center;
        const size = 16;

        const localPoints: [number, number][] = [
            [-size / 2, centerOffsetY - size / 2],
            [ size / 2, centerOffsetY - size / 2],
            [ size / 2, centerOffsetY + size / 2],
            [-size / 2, centerOffsetY + size / 2],
        ];

        return localPoints.map(([ox, oy]) =>
            scannerRotatedPoint(
                x,
                y,
                ox,
                oy,
                angle
            )
        );
    };

    const scannerPointsString = (
        points: [number, number][]
    ) =>
        points
            .map(([x, y]) => `${x},${y}`)
            .join(" ");

    const handleScannerCanvasClick = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            !setupComplete ||
            drawingTool !== "scanner"
        ) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            return;
        }

        /*
         * First click:
         * establish the Scanner pivot.
         */
        if (!scannerPlacementAnchor) {
            if (
                geofenceComplete &&
                geofencePoints.length >= 3 &&
                !pointInsidePolygon(
                    coords,
                    geofencePoints
                )
            ) {
                return;
            }

            setScannerPlacementAnchor(coords);
            setScannerPreviewAngle(0);
            return;
        }

        /*
         * Second click:
         * calculate snapped heading and lock Scanner.
         */
        const angle =
            scannerHeading45(
                scannerPlacementAnchor,
                coords
            );

        const nextNumber =
            scanners.reduce((largest, scanner) => {
                const match = scanner.id.match(/^SC(\d+)$/);

                if (!match) {
                    return largest;
                }

                return Math.max(
                    largest,
                    Number(match[1])
                );
            }, 0) + 1;

        setScanners((previous) => [
            ...previous,
            {
                id: `SC${nextNumber}`,
                px: scannerPlacementAnchor,
                orientationDeg: angle,
            },
        ]);

        setScannerPlacementAnchor(null);
        setScannerPreviewAngle(0);
    };

    const updateScannerPreview = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            drawingTool !== "scanner" ||
            !scannerPlacementAnchor
        ) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            return;
        }

        setScannerPreviewAngle(
            scannerHeading45(
                scannerPlacementAnchor,
                coords
            )
        );
    };

    const pointHitsPod = (
        point: [number, number]
    ) => {
        if (!pod) {
            return false;
        }

        const half = 12;

        return (
            point[0] >= pod.px[0] - half &&
            point[0] <= pod.px[0] + half &&
            point[1] >= pod.px[1] - half &&
            point[1] <= pod.px[1] + half
        );
    };

    const handlePodCanvasClick = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            !setupComplete ||
            drawingTool !== "pod"
        ) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            return;
        }

        /*
         * Clicking the existing Pod begins the drag workflow.
         * Do not relocate it from the click handler.
         */
        if (pointHitsPod(coords)) {
            return;
        }

        if (
            geofenceComplete &&
            geofencePoints.length >= 3 &&
            !pointInsidePolygon(coords, geofencePoints)
        ) {
            return;
        }

        if (!pod) {
            setPod({
                px: coords,
            });
        } else {
            setPod({
                ...pod,
                px: coords,
            });
        }
    };

    const handlePodMouseDown = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            !setupComplete ||
            drawingTool !== "pod" ||
            event.button !== 0 ||
            !pod
        ) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords || !pointHitsPod(coords)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        podDragStartRef.current = [
            pod.px[0],
            pod.px[1],
        ];

        setIsDraggingPod(true);
    };

    const handlePodMouseMove = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            drawingTool !== "pod" ||
            !isDraggingPod ||
            !pod
        ) {
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            return;
        }

        if (
            geofenceComplete &&
            geofencePoints.length >= 3 &&
            !pointInsidePolygon(coords, geofencePoints)
        ) {
            return;
        }

        setPod({
            ...pod,
            px: coords,
        });
    };

    const handlePodMouseUp = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            drawingTool !== "pod" ||
            !isDraggingPod
        ) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        setIsDraggingPod(false);
        podDragStartRef.current = null;
    };

    const handleCanvasMouseMove = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        // Trailer scale calibration preview.
        if (setupStep === "scale") {
            const startPoint = scaleDragStartRef.current;

            if (startPoint) {
                const coords = getImageCoordinates(event);

                if (!coords) {
                    return;
                }

                const snappedEnd = snapEndpointDegrees(
                    startPoint,
                    coords,
                    45
                );

                setScaleDragEnd(snappedEnd);
                return;
            }
        }

        // Existing geofence preview.
        if (
            setupStep !== "geofence" ||
            geofencePoints.length === 0
        ) {
            setGeofencePreview(null);
            return;
        }

        const coords = getImageCoordinates(event);

        if (!coords) {
            setGeofencePreview(null);
            return;
        }

        const snapped =
            snapEndpointDegrees(
                geofencePoints[geofencePoints.length - 1],
                coords,
                15
            );

        setGeofencePreview(snapped);
    };

    const handleCanvasDoubleClick = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (
            setupComplete &&
            drawingTool === "overflow"
        ) {
            event.preventDefault();
            event.stopPropagation();
            finishOverflow();
            return;
        }
        if (
            setupComplete &&
            drawingTool === "building"
        ) {
            event.preventDefault();
            event.stopPropagation();
            finishBuilding();
            return;
        }

        if (
            setupComplete &&
            drawingTool === "no-parking"
        ) {
            event.preventDefault();
            event.stopPropagation();
            finishNoParking();
            return;
        }

        if (setupStep !== "geofence") {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        /*
         * A browser double-click also produces click events.
         * handleCanvasClick below ignores event.detail > 1 so the
         * second click is not accidentally added as another vertex.
         */
        finishGeofence();
    };

    const handleCanvasClick = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        /*
         * Polygon tools finish on double-click.
         * Ignore the extra click generated by the browser so it does
         * not become another polygon vertex.
         */
        if (
            event.detail > 1 &&
            (
                drawingTool === "building" ||
                drawingTool === "no-parking" ||
                drawingTool === "overflow"
            )
        ) {
            return;
        }

        if (setupStep === "geofence") {
            /*
             * Ignore the second click generated as part of a double-click.
             * The double-click itself is handled separately and closes
             * the polygon.
             */
            if (event.detail > 1) {
                return;
            }

            const coords = getImageCoordinates(event);

            if (!coords) {
                return;
            }

            setGeofencePoints((previous) => {
                const snappedPoint =
                    snapPolygonPoint15(previous, coords);

                return [
                    ...previous,
                    snappedPoint,
                ];
            });

            setGeofenceError("");
            return;
        }

        const gpsIndex = getGpsIndex();

        if (gpsIndex < 0 || !imageRef.current) {
            return;
        }

        const lat = Number(gpsInputs[gpsIndex].lat);
        const lon = Number(gpsInputs[gpsIndex].lon);

        if (
            gpsInputs[gpsIndex].lat.trim() === "" ||
            gpsInputs[gpsIndex].lon.trim() === "" ||
            !Number.isFinite(lat) ||
            !Number.isFinite(lon)
        ) {
            setGpsError(
                `Enter valid latitude and longitude for P${gpsIndex + 1} before selecting the point.`
            );
            return;
        }

        const imageRect = imageRef.current.getBoundingClientRect();

        if (
            event.clientX < imageRect.left ||
            event.clientX > imageRect.right ||
            event.clientY < imageRect.top ||
            event.clientY > imageRect.bottom
        ) {
            return;
        }

        const x =
            ((event.clientX - imageRect.left) / imageRect.width) *
            imageRef.current.naturalWidth;

        const y =
            ((event.clientY - imageRect.top) / imageRect.height) *
            imageRef.current.naturalHeight;

        const point: GPSPoint = {
            x,
            y,
            lat,
            lon,
        };

        const updatedPoints = gpsPoints.map((existing, i) =>
            i === gpsIndex ? point : existing
        );

        setGpsPoints(updatedPoints);
        setGpsError("");

        if (gpsIndex < 3) {
            setSetupStep(
                ["gps-p2", "gps-p3", "gps-p4"][gpsIndex] as SetupStep
            );
            return;
        }

        try {
            const completedPoints = updatedPoints.filter(
                (p): p is GPSPoint => p !== null
            );

            if (completedPoints.length !== 4) {
                throw new Error("All four GPS points are required.");
            }

            const calibration =
                computeGeoCalibration(completedPoints);

            setGeoCalibration(calibration);
            if (isRecalibratingGPS) {
                setIsRecalibratingGPS(false);
                setSetupStep("complete");
            } else {
                setSetupStep("geofence");
            }
        } catch (error) {
            setGeoCalibration(null);

            setGpsError(
                error instanceof Error
                    ? error.message
                    : "GPS calibration failed."
            );

            setSetupStep("gps-p4");
        }
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Do not intercept keyboard input while typing in a form field.
            const target = event.target as HTMLElement | null;

            if (
                target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement ||
                target instanceof HTMLSelectElement ||
                target?.isContentEditable
            ) {
                return;
            }

            // Escape cancels the current in-progress operation.
            // It does NOT undo completed work.
            if (event.key === "Escape") {
                event.preventDefault();

                // Cancel an unfinished parking row.
                if (drawingTool === "parking") {
                    resetParkingRow();
                }

                // Cancel an unfinished section rectangle.
                if (drawingTool === "section") {
                    sectionDragStartRef.current = null;
                    setSectionDragStart(null);
                    setSectionDragEnd(null);
                }

                // Cancel an unfinished Building polygon.
                if (drawingTool === "building") {
                    setBuildingPoints([]);
                    setBuildingPreview(null);
                }

                // Cancel an unfinished No-Parking polygon.
                if (drawingTool === "no-parking") {
                    setNoParkingPoints([]);
                    setNoParkingPreview(null);
                }

                // Cancel an unfinished Crosswalk.
                if (drawingTool === "crosswalk") {
                    setCrosswalkStart(null);
                    setCrosswalkPreview(null);
                }

                // Cancel/clear an active selection.
                if (drawingTool === "select") {
                    clearSelection();
                    selectionStartRef.current = null;
                }

                // Cancel an unfinished scale measurement.
                if (setupStep === "scale") {
                    scaleDragStartRef.current = null;
                    setScaleDragStart(null);
                    setScaleDragEnd(null);
                    setScalePxLength(null);
                    setScaleError("");
                }

                // Cancel the current geofence drawing operation.
                // Existing GPS calibration remains untouched.
                if (
                    setupStep === "geofence" &&
                    !geofenceComplete
                ) {
                    setGeofencePoints([]);
                    setGeofencePreview(null);
                    setGeofenceError("");
                }

                return;
            }

            // Delete any selected yard object or parking space.
            if (
                drawingTool === "select" &&
                (selectedParkingIds.length > 0 ||
                    selectedYardObject !== null) &&
                (event.key === "Delete" ||
                    event.key === "Backspace")
            ) {
                event.preventDefault();
                deleteSelectedItems();
                return;
            }

            // Finish a Building polygon with Enter.
            if (
                drawingTool === "building" &&
                event.key === "Enter"
            ) {
                event.preventDefault();
                finishBuilding();
                return;
            }

            // Finish a No-Parking polygon with Enter.
            if (
                drawingTool === "no-parking" &&
                event.key === "Enter"
            ) {
                event.preventDefault();
                finishNoParking();
                return;
            }

            // Move selected parking spaces with the arrow keys.
            if (
                selectedParkingIds.length > 0 &&
                ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(
                    event.key
                )
            ) {
                event.preventDefault();

                const distance = event.shiftKey ? 5 : 1;

                let dx = 0;
                let dy = 0;

                switch (event.key) {
                    case "ArrowLeft":
                        dx = -distance;
                        break;

                    case "ArrowRight":
                        dx = distance;
                        break;

                    case "ArrowUp":
                        dy = -distance;
                        break;

                    case "ArrowDown":
                        dy = distance;
                        break;
                }

                setParkingSpaces((previous) => {
                    const spacesBeforeMove =
                        previous
                            .filter((space) =>
                                selectedParkingIds.includes(
                                    space.id
                                )
                            )
                            .map((space) => ({
                                ...space,
                                anchor: [
                                    space.anchor[0],
                                    space.anchor[1],
                                ] as [number, number],
                            }));

                    if (spacesBeforeMove.length > 0) {
                        setUndoStack((history) => [
                            ...history,
                            {
                                type: "move-parking",
                                spaces: spacesBeforeMove,
                            },
                        ]);
                    }

                    return previous.map((space) => {
                        if (!selectedParkingIds.includes(space.id)) {
                            return space;
                        }

                        return {
                            ...space,
                            anchor: [
                                space.anchor[0] + dx,
                                space.anchor[1] + dy,
                            ] as [number, number],
                        };
                    });
                });

                return;
            }

            if (
                event.key === "Enter" &&
                setupStep === "geofence" &&
                geofencePoints.length >= 3
            ) {
                setGeofenceComplete(true);
                setGeofenceError("");
                setSetupStep("scale");
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [
        setupStep,
        geofencePoints,
        selectedParkingIds,
        drawingTool,
        noParkingPoints,
        noParkingZones,
        buildingPoints,
        buildings,
        crosswalkStart,
        crosswalks,
    ]);

    const getCurrentStepNumber = () => {
        if (setupStep === "ready") {
            return 2;
        }

        const index = setupSteps.findIndex(
            (step) => step.key === setupStep
        );

        return index >= 0 ? index + 1 : 1;
    };

    const getCurrentTitle = () => {
        switch (setupStep) {
            case "yard-info":
                return "Yard Information";
            case "load-image":
                return "Load Yard Image";
            case "ready":
                return "Yard Image Loaded";
            case "gps-p1":
                return "Set GPS Point P1";
            case "gps-p2":
                return "Set GPS Point P2";
            case "gps-p3":
                return "Set GPS Point P3";
            case "gps-p4":
                return "Set GPS Point P4";
            case "geofence":
                return "Draw Geofence";
            case "scale":
                return "Calibrate Scale";
            case "complete":
                return "Setup Complete";
        }
    };

    const getCurrentDescription = () => {
        switch (setupStep) {
            case "yard-info":
                return "Enter the yard information and operating altitude limits.";
            case "load-image":
                return "Upload an aerial image of the yard to begin setup.";
            case "ready":
                return "Image loaded successfully. Start the required setup wizard to continue.";
            case "gps-p1":
                return "Enter the coordinates for P1, then select the corresponding point on the yard image.";
            case "gps-p2":
                return "Enter the coordinates for P2, then select the corresponding point on the yard image.";
            case "gps-p3":
                return "Enter the coordinates for P3, then select the corresponding point on the yard image.";
            case "gps-p4":
                return "Enter the coordinates for P4, then select the corresponding point on the yard image.";
            case "geofence":
                return "Define the yard geofence boundary.";
            case "scale":
                return "Calibrate the yard scale.";
            case "complete":
                return "Required yard setup is complete. Yard editing tools are now available.";
        }
    };

    const getStepIndex = (step: SetupStep) => {
        if (step === "ready") {
            return 1;
        }

        return setupSteps.findIndex((item) => item.key === step);
    };

    const currentProgressIndex = getStepIndex(setupStep);

    const handleLoadExistingYard = async () => {
        if (isLoadingExistingYard || isSubmitting) return;

        setIsLoadingExistingYard(true);
        setExistingYardNeedsValidation(true);
        setLoadedYardEtag(null);
        setLoadedAssignedYardName(null);
        setSubmissionError("");
        setSubmissionMessage("");

        try {
            const response = await getYardLayout();

            if (!response.success || !response.layout) {
                throw new Error("No existing yard configuration was found.");
            }

            if (
                typeof response.etag !== "string" ||
                !/^"[A-Fa-f0-9]{32}(?:-[0-9]+)?"$/.test(response.etag)
            ) {
                throw new Error(
                    "Saved yard version information is missing or invalid."
                );
            }

            const savedYard = response.layout;

            const configuration = loadYardConfiguration(savedYard);
            const geometry = loadYardGeometry(savedYard);
            const parking = loadParkingSpaces(
                savedYard,
                configuration.metersPerPx
            );

            if (
                !Number.isFinite(configuration.imageWidth) ||
                !Number.isFinite(configuration.imageHeight) ||
                configuration.imageWidth <= 0 ||
                configuration.imageHeight <= 0
            ) {
                throw new Error("Saved yard image dimensions are invalid.");
            }

            // Retrieve and validate the original aerial image automatically.
            const originalImageUrl = await getOriginalYardImage();

            try {
                const originalImage = new Image();

                await new Promise<void>((resolve, reject) => {
                    originalImage.onload = () => resolve();
                    originalImage.onerror = () =>
                        reject(new Error(
                            "Unable to decode the saved aerial image."
                        ));
                    originalImage.src = originalImageUrl;
                });

                if (
                    originalImage.naturalWidth !== configuration.imageWidth ||
                    originalImage.naturalHeight !== configuration.imageHeight
                ) {
                    throw new Error(
                        "Saved aerial image dimensions do not match setup.json."
                    );
                }

                setYardImage((previous) => {
                    if (previous?.startsWith("blob:")) {
                        URL.revokeObjectURL(previous);
                    }
                    return originalImageUrl;
                });

                setYardImageName("Original aerial yard image");

            } catch (imageError) {
                URL.revokeObjectURL(originalImageUrl);
                throw imageError;
            }

            setExpectedYardImageSize({
                width: configuration.imageWidth,
                height: configuration.imageHeight,
            });

            // Reconstruct GPS calibration before updating editor state.
            const calibration = computeGeoCalibration(
                configuration.gpsPoints
            );

            // Restore the saved configuration and editable geometry.
            setYardInfo(configuration.yardInfo);
            setGpsPoints(configuration.gpsPoints);
            setGpsInputs(
                configuration.gpsPoints.map((point) => ({
                    lat: String(point.lat),
                    lon: String(point.lon),
                }))
            );
            setGeoCalibration(calibration);

            setGeofencePoints(configuration.geofencePoints);
            setGeofenceComplete(true);

            setScalePxLength(configuration.scalePxLength);
            setScaleFeet(configuration.scaleFeet);
            setMetersPerPx(configuration.metersPerPx);

            setParkingSpaces(parking);
            setSections(geometry.sections);
            setBuildings(geometry.buildings);
            setScanners(geometry.scanners);
            setOverflowBlocks(geometry.overflowBlocks);
            setNoParkingZones(geometry.noParkingZones);
            setCrosswalks(geometry.crosswalks);
            setPod(configuration.pod);

            // Final validation before authorizing edits.
            if (!configuration.yardInfo.name.trim()) {
                throw new Error("Saved yard name is missing.");
            }

            if (
                !Number.isFinite(configuration.scalePxLength) ||
                configuration.scalePxLength <= 0 ||
                !Number.isFinite(Number(configuration.scaleFeet)) ||
                Number(configuration.scaleFeet) <= 0
            ) {
                throw new Error("Saved yard scale measurements are invalid.");
            }

            if (
                configuration.geofencePoints.length < 3 ||
                configuration.geofencePoints.some(
                    (point) =>
                        !Array.isArray(point) ||
                        point.length !== 2 ||
                        !point.every(
                            (value) =>
                                typeof value === "number" &&
                                Number.isFinite(value)
                        )
                )
            ) {
                throw new Error("Saved yard geofence is invalid.");
            }

            // The image, GPS calibration and geometry have been restored.
            setLoadedAssignedYardName(response.yardName);
            setLoadedYardEtag(response.etag);
            setSetupStep("complete");
            setExistingYardNeedsValidation(false);

            console.log("Existing yard validated:", {
                yardName: configuration.yardInfo.name,
                sections: geometry.sections.length,
                buildings: geometry.buildings.length,
                parkingSpaces: parking.length,
                scanners: geometry.scanners.length,
                hasPod: configuration.pod !== null,
            });

            setSubmissionMessage(
                "Existing yard loaded and validated. Save Yard is available."
            );
        } catch (error) {
            setSubmissionError(
                error instanceof Error
                    ? error.message
                    : "Unable to load existing yard."
            );
        } finally {
            setIsLoadingExistingYard(false);
        }
    };

    const handleRetryImageUpload = async () => {
        if (
            !imageUploadNeedsRetry ||
            isSubmitting ||
            isLoadingExistingYard
        ) {
            return;
        }

        setIsSubmitting(true);
        setSubmissionError("");
        setSubmissionMessage("");

        try {
            if (!yardImage) {
                throw new Error("Original aerial image is unavailable.");
            }

            const response = await fetch(yardImage);

            if (!response.ok) {
                throw new Error("Unable to read the aerial image.");
            }

            const imageBlob = await response.blob();

            // Only upload the image. Never resubmit yard configuration.
            try {
                await uploadOriginalYardImage(imageBlob);
            } catch (uploadError: any) {
                if (uploadError?.response?.status !== 409) {
                    throw uploadError;
                }

                // The image may have been stored before the response was lost.
                // Compare the actual bytes, not just the dimensions.
                const storedUrl = await getOriginalYardImage();

                try {
                    const storedResponse = await fetch(storedUrl);
                    if (!storedResponse.ok) {
                        throw new Error("Unable to verify stored image.");
                    }

                    const storedBlob = await storedResponse.blob();
                    const localBytes = new Uint8Array(
                        await imageBlob.arrayBuffer()
                    );
                    const storedBytes = new Uint8Array(
                        await storedBlob.arrayBuffer()
                    );

                    if (
                        localBytes.length !== storedBytes.length ||
                        !localBytes.every(
                            (value, index) => value === storedBytes[index]
                        )
                    ) {
                        throw new Error(
                            "A different original aerial image already exists. " +
                            "The existing image was not replaced."
                        );
                    }
                } finally {
                    URL.revokeObjectURL(storedUrl);
                }
            }

            setImageUploadNeedsRetry(false);
            setSubmissionMessage(
                "Original aerial image uploaded successfully."
            );

            navigate("/dashboard");

        } catch (error) {
            setSubmissionError(
                error instanceof Error
                    ? error.message
                    : "Unable to retry the image upload."
            );
        } finally {
            setIsSubmitting(false);
        }
    };


    const [loadedAssignedYardName, setLoadedAssignedYardName] = useState<string | null>(null);
    const [showDeleteYardDialog, setShowDeleteYardDialog] = useState(false);
    const [deleteYardConfirmation, setDeleteYardConfirmation] = useState("");
    const [isDeletingYard, setIsDeletingYard] = useState(false);

    const handleDeleteYard = async () => {
        if (
            deleteYardConfirmation !== "DELETE" ||
            isDeletingYard ||
            isSubmitting ||
            isLoadingExistingYard ||
            !loadedAssignedYardName?.trim() ||
            !loadedYardEtag ||
            existingYardNeedsValidation
        ) return;

        setIsDeletingYard(true);
        setSubmissionError("");
        setSubmissionMessage("");

        try {
            const result = await deleteYard(loadedAssignedYardName!, "DELETE");
            if (!result.success) throw new Error("Deletion not confirmed.");

            setShowDeleteYardDialog(false);
            setDeleteYardConfirmation("");
            setLoadedYardEtag(null);
            setLoadedAssignedYardName(null);
            setSubmissionMessage(
                "Yard configuration deleted. Reload the page before creating a new yard."
            );
        } catch (error: any) {
            setSubmissionError(
                error?.response?.data?.error ||
                error?.message ||
                "Unable to delete yard."
            );
        } finally {
            setIsDeletingYard(false);
        }
    };

    const handleSaveYard = async () => {
        if (
            !canSave ||
            !loadedYardEtag ||
            existingYardNeedsValidation ||
            isSubmitting ||
            isLoadingExistingYard
        ) {
            return;
        }

        setIsSubmitting(true);
        setSubmissionError("");
        setSubmissionMessage("");

        try {
            if (!imageRef.current) {
                throw new Error("Yard image is not loaded.");
            }

            const setupData = buildSetupData({
                yardInfo,
                imageWidth: imageRef.current.naturalWidth,
                imageHeight: imageRef.current.naturalHeight,
                gpsPoints,
                geofencePoints,
                scalePxLength,
                scaleFeet,
                metersPerPx,
                parkingSpaces,
                sections,
                buildings,
                pod,
                scanners,
                overflowBlocks,
                noParkingZones,
                crosswalks,
            });

            await updateYard(setupData, loadedYardEtag);

            // Require a fresh version before any subsequent save.
            setLoadedYardEtag(null);

            setSubmissionMessage(
                "Yard configuration saved successfully. " +
                "Reload the yard before making another save."
            );

        } catch (error: any) {
            setSubmissionError(
                error?.response?.data?.error ||
                error?.message ||
                "Unable to save yard configuration."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmitYard = async () => {
        if (
            !canSubmit ||
            isLoadingExistingYard ||
            existingYardNeedsValidation ||
            imageUploadNeedsRetry
        ) {
            return;
        }

        setIsSubmitting(true);
        setImageUploadNeedsRetry(false);
        setSubmissionMessage("");
        setSubmissionError("");

        try {
            if (!imageRef.current) {
                throw new Error("Yard image is not loaded.");
            }

            if (metersPerPx === null || metersPerPx <= 0) {
                throw new Error("Yard scale calibration is incomplete.");
            }

            const validGPSPoints = gpsPoints.filter(
                (point): point is SubmissionGPSPoint => point !== null
            );

            if (validGPSPoints.length !== 4) {
                throw new Error("Four GPS calibration points are required.");
            }

            const projection = buildGPSProjection(validGPSPoints);

            const setupData = buildSetupData({
                yardInfo,
                imageWidth: imageRef.current.naturalWidth,
                imageHeight: imageRef.current.naturalHeight,
                gpsPoints,
                geofencePoints,
                scalePxLength,
                scaleFeet,
                metersPerPx,
                parkingSpaces,
                sections,
                buildings,
                pod,
                scanners,
                overflowBlocks,
                noParkingZones,
                crosswalks,
            });

            const parkingData = buildParkingData(
                parkingSpaces,
                metersPerPx,
                projection
            );

            const result = await submitYard({
                yardName: yardInfo.name,
                setupData,
                parkingData,
            });

            // Save the original aerial image after setup.json exists.
            if (!yardImage) {
                throw new Error(
                    "Yard configuration saved, but the original aerial image is missing."
                );
            }

            let imageBlob: Blob;

            try {
                const imageResponse = await fetch(yardImage);
                if (!imageResponse.ok) {
                    throw new Error("Unable to read the aerial image.");
                }

                imageBlob = await imageResponse.blob();

                try {
                    await uploadOriginalYardImage(imageBlob);
                } catch (uploadError: any) {
                    if (uploadError?.response?.status !== 409) {
                        throw uploadError;
                    }

                    // Verify that the existing S3 image is identical.
                    const storedUrl = await getOriginalYardImage();

                    try {
                        const storedResponse = await fetch(storedUrl);

                        if (!storedResponse.ok) {
                            throw new Error(
                                "Unable to verify the stored aerial image."
                            );
                        }

                        const storedBlob = await storedResponse.blob();

                        const localBytes = new Uint8Array(
                            await imageBlob.arrayBuffer()
                        );

                        const storedBytes = new Uint8Array(
                            await storedBlob.arrayBuffer()
                        );

                        if (
                            localBytes.length !== storedBytes.length ||
                            !localBytes.every(
                                (value, index) =>
                                    value === storedBytes[index]
                            )
                        ) {
                            throw new Error(
                                "A different original aerial image already exists. " +
                                "The existing image was not replaced."
                            );
                        }
                    } finally {
                        URL.revokeObjectURL(storedUrl);
                    }
                }
            } catch (imageError) {
                setImageUploadNeedsRetry(true);
                throw new Error(
                    "Yard configuration was saved, but the original aerial " +
                    "image could not be uploaded. Please retry image upload " +
                    "without resubmitting the yard. " +
                    (imageError instanceof Error ? imageError.message : "")
                );
            }

            setSubmissionMessage(
                `Yard "${result.yardName}" and original aerial image saved successfully.`
            );

            navigate("/dashboard");
        } catch (error) {
            setSubmissionError(
                error instanceof Error
                    ? error.message
                    : "Unable to submit yard."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex h-full flex-col overflow-hidden bg-gray-4">

            {/* Designer Header */}
            <div className="flex items-center justify-between gap-4 border-b bg-white px-5 py-3">
                <div className="min-w-0">
                    <h1 className="text-xl font-[Geist-Bold] text-green">
                        Yard Designer
                    </h1>

                    <p className="text-sm text-gray-2">
                        Configure and register a Willow Yard
                    </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                    <button className="rounded-lg border bg-white px-4 py-2 text-sm font-[Geist-SemiBold] text-gray-2">
                        Cancel
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            setDeleteYardConfirmation("");
                            setShowDeleteYardDialog(true);
                        }}
                        disabled={
                            !yardInfo.name.trim() ||
                            isSubmitting ||
                            isLoadingExistingYard ||
                            isDeletingYard ||
                            !loadedYardEtag ||
                            existingYardNeedsValidation
                        }
                        className="rounded-lg border border-red-600 px-4 py-2 text-sm font-[Geist-SemiBold] text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Delete Yard
                    </button>

                    <button
                        type="button"
                        onClick={handleSaveYard}
                        disabled={!canSave || !loadedYardEtag || isSubmitting || imageUploadNeedsRetry}
                        className={`rounded-lg px-4 py-2 text-sm font-[Geist-SemiBold] text-white ${
                            canSave
                                ? "bg-green"
                                : "cursor-not-allowed bg-gray-300"
                        }`}
                    >
                        Save Yard
                    </button>

                    <button
                        type="button"
                        onClick={handleLoadExistingYard}
                        disabled={isLoadingExistingYard || isSubmitting}
                        className={`rounded-lg border px-4 py-2 text-sm font-[Geist-SemiBold] ${
                            isLoadingExistingYard || isSubmitting
                                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                : "bg-white text-green"
                        }`}
                    >
                        {isLoadingExistingYard
                            ? "Loading Yard..."
                            : "Load Existing Yard"}
                    </button>

                    {imageUploadNeedsRetry && (
                        <button
                            type="button"
                            onClick={handleRetryImageUpload}
                            disabled={isSubmitting || isLoadingExistingYard}
                            className="rounded-lg bg-green px-4 py-2 text-sm font-[Geist-SemiBold] text-white disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isSubmitting
                                ? "Uploading Image..."
                                : "Retry Image Upload"}
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={handleSubmitYard}
                        disabled={!canSubmit}
                        className={`rounded-lg px-4 py-2 text-sm font-[Geist-SemiBold] text-white ${
                            canSubmit
                                ? "bg-blue"
                                : "cursor-not-allowed bg-gray-300"
                        }`}
                    >
                        {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                </div>
                {(submissionMessage || submissionError) && (
                    <div
                        role="status"
                        className={`text-sm ${
                            submissionError ? "text-red-600" : "text-green-600"
                        }`}
                    >
                        {submissionError || submissionMessage}
                    </div>
                )}
            </div>

            {/* Current Step */}
            <div className="border-b bg-white px-5 py-3">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green text-sm font-bold text-white">
                            {getCurrentStepNumber()}
                        </div>

                        <div>
                            <p className="text-sm font-[Geist-SemiBold] text-black">
                                {getCurrentTitle()}
                            </p>

                            <p className="text-xs text-gray-2">
                                {getCurrentDescription()}
                            </p>
                        </div>
                    </div>

                    {setupStep === "ready" && (
                        <button
                            onClick={startSetupWizard}
                            className="flex shrink-0 items-center gap-2 rounded-lg bg-green px-4 py-2 text-sm font-[Geist-SemiBold] text-white"
                        >
                            <Icon
                                icon="material-symbols:play-arrow-rounded"
                                width={18}
                                height={18}
                            />
                            Start Setup Wizard
                        </button>
                    )}
                </div>
            </div>

            {/* Tool Bar */}
            <div className="flex items-center gap-2 overflow-x-auto border-b bg-white px-4 py-2">

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    onChange={handleLoadImage}
                />

                <ToolButton
                    icon="material-symbols:upload"
                    label={imageLoaded ? "Change Image" : "Load Image"}
                    onClick={() => fileInputRef.current?.click()}
                />

                <Divider />

                {setupComplete && (
                    <ToolButton
                        icon="material-symbols:my-location"
                        label="Recalibrate GPS"
                        onClick={() => {
                            if (!window.confirm("Recalibrate GPS points P1–P4? Existing yard objects will be preserved.")) return;
                            setIsRecalibratingGPS(true);
                            setGpsError("");
                            setDrawingTool("select");
                            setSetupStep("gps-p1");
                        }}
                    />
                )}

                <ToolButton
                    icon="mdi:hand-back-right-outline"
                    label="Pan"
                    disabled={!yardImage}
                    active={drawingTool === "pan"}
                    onClick={() => {
                        setDrawingTool("pan");
                        clearSelection();
                        resetParkingRow();
                    }}
                />
                <ToolButton
                    icon="material-symbols:near-me-outline"
                    label="Select"
                    disabled={!canUseDrawingTools}
                    active={drawingTool === "select"}
                    onClick={() => {
                        setDrawingTool("select");
                        clearSelection();
                        resetParkingRow();
                    }}
                />
                <ToolButton
                    icon="mdi:ruler"
                    label="Measure"
                    disabled={!canUseDrawingTools}
                />
                <ToolButton
                    icon="material-symbols:crop-square-outline"
                    label="Section"
                    disabled={!canUseDrawingTools}
                    active={drawingTool === "section"}
                    onClick={() => {
                        setDrawingTool("section");
                        clearSelection();
                        resetParkingRow();
                    }}
                />
                <ToolButton
                    icon="material-symbols:grid-view-outline"
                    label="Overflow"
                    disabled={!canUseDrawingTools}
                    active={drawingTool === "overflow"}
                    onClick={() => {
                        setDrawingTool("overflow");
                        clearSelection();
                        resetParkingRow();

                    }}
                />
                <ToolButton
                    icon="material-symbols:domain"
                    label="Building"
                    disabled={!canUseDrawingTools}
                    active={drawingTool === "building"}
                    onClick={() => {
                        setDrawingTool("building");
                        clearSelection();
                        resetParkingRow();
                    }}
                />
                <ToolButton
                    icon="material-symbols:block"
                    label="No-Parking"
                    disabled={!canUseDrawingTools}
                    active={drawingTool === "no-parking"}
                    onClick={() => {
                        setDrawingTool("no-parking");
                        clearSelection();
                        resetParkingRow();
                    }}
                />
                <ToolButton
                    icon="material-symbols:pedal-bike"
                    label="Crosswalk"
                    disabled={!canUseDrawingTools}
                    active={drawingTool === "crosswalk"}
                    onClick={() => {
                        setDrawingTool("crosswalk");
                        clearSelection();
                        resetParkingRow();
                    }}
                />
                <ToolButton
                    icon="material-symbols:location-on-outline"
                    label="Pod"
                    disabled={!canUseDrawingTools}
                    active={drawingTool === "pod"}
                    onClick={() => {
                        setDrawingTool("pod");
                        clearSelection();
                        resetParkingRow();
                    }}
                />
                <ToolButton
                    icon="material-symbols:local-parking"
                    label="Parking"
                    disabled={!canUseDrawingTools}
                    active={drawingTool === "parking"}
                    onClick={() => {
                        setDrawingTool("parking");
                        resetParkingRow();
                    }}
                />
                <ToolButton
                    icon="material-symbols:sensors"
                    label="Scanner"
                    disabled={!canUseDrawingTools}
                    active={drawingTool === "scanner"}
                    onClick={() => {
                        setDrawingTool("scanner");
                        clearSelection();
                        resetParkingRow();
                    }}
                />

                <Divider />

                <ToolButton
                    icon="material-symbols:delete-outline"
                    label="Delete Selected"
                    disabled={
                        selectedParkingIds.length === 0 &&
                        selectedYardObject === null
                    }
                    onClick={deleteSelectedItems}
                />

                <ToolButton
                    icon="material-symbols:undo"
                    label="Undo"
                    disabled={undoStack.length === 0}
                    onClick={undoLastAction}
                />
            </div>

            {/* Selected parking-space GPS coordinates */}
            <div className="shrink-0 border-b bg-white">
            {selectedParkingIds.length === 1 ? (() => {
                const space = parkingSpaces.find(
                    (item) => item.id === selectedParkingIds[0]
                );

                if (!space || metersPerPx === null || metersPerPx <= 0) {
                    return null;
                }

                let coordinates: [number, number] | null = null;

                try {
                    const anchors = gpsPoints.filter(
                        (point): point is GPSPoint => point !== null
                    );

                    if (anchors.length === 4) {
                        const projection = buildGPSProjection(anchors);
                        const lengthPx = space.lengthFt * 0.3048 / metersPerPx;
                        const angle = space.angle * Math.PI / 180;

                        const centerX =
                            space.anchor[0] - Math.sin(angle) * lengthPx / 2;
                        const centerY =
                            space.anchor[1] + Math.cos(angle) * lengthPx / 2;

                        coordinates = pixelToGPS(
                            [centerX, centerY],
                            projection
                        );
                    }
                } catch {
                    // Invalid calibration must not produce misleading coordinates.
                }

                return (
                    <div className="flex min-h-[49px] flex-wrap items-center gap-4 px-5 py-3 text-sm">
                        <span className="font-[Geist-SemiBold] text-green">
                            {space.label} — Center GPS
                        </span>

                        {coordinates ? (
                            <>
                                <span>
                                    Latitude: {coordinates[0].toFixed(8)}
                                </span>
                                <span>
                                    Longitude: {coordinates[1].toFixed(8)}
                                </span>
                                <button
                                    type="button"
                                    className="rounded-lg border px-3 py-1 text-green"
                                    onClick={() => {
                                        if (coordinates) {
                                            void navigator.clipboard.writeText(
                                                `${coordinates[0].toFixed(8)}, ${coordinates[1].toFixed(8)}`
                                            );
                                        }
                                    }}
                                >
                                    Copy Coordinates
                                </button>
                            </>
                        ) : (
                            <span className="text-red-600">
                                GPS calibration unavailable
                            </span>
                        )}
                    </div>
                );
            })() : (
                <div className="flex min-h-[49px] items-center px-5 py-3 text-sm text-gray-500">
                    Select a parking space to view its GPS coordinates.
                </div>
            )}
            </div>

            {/* Main Workspace */}
            <div className="flex min-h-0 flex-1">

                {/* Canvas */}
                <div className="flex min-w-0 flex-1 flex-col p-4">

                    <div
                        ref={imageContainerRef}
                        onClick={(event) => {
                            if (drawingTool === "pan") {
                                suppressPanClickRef.current = false;
                                return;
                            }
                            if (suppressPanClickRef.current) {
                                suppressPanClickRef.current = false;
                                event.preventDefault();
                                event.stopPropagation();
                                return;
                            }
                            if (
                                setupComplete &&
                                drawingTool === "parking"
                            ) {
                                handleParkingCanvasClick(event);
                                return;
                            }

                            if (
                                setupComplete &&
                                drawingTool === "building"
                            ) {
                                handleBuildingCanvasClick(event);
                                return;
                            }

                            if (
                                setupComplete &&
                                drawingTool === "no-parking"
                            ) {
                                handleNoParkingCanvasClick(event);
                                return;
                            }

                            if (
                                setupComplete &&
                                drawingTool === "crosswalk"
                            ) {
                                handleCrosswalkCanvasClick(event);
                                return;
                            }

                            if (
                                setupComplete &&
                                drawingTool === "pod"
                            ) {
                                handlePodCanvasClick(event);
                                return;
                            }

                            if (
                                setupComplete &&
                                drawingTool === "scanner"
                            ) {
                                handleScannerCanvasClick(event);
                                return;
                            }

                            if (
                                setupComplete &&
                                drawingTool === "overflow"
                            ) {
                                handleOverflowCanvasClick(event);
                                return;
                            }

                            handleCanvasClick(event);
                        }}
                        onDoubleClick={(event) => {
                            if (drawingTool === "pan") return;
                            handleCanvasDoubleClick(event);
                        }}
                        onWheel={handleCanvasWheel}
                        onMouseDown={(event) => {
                            if (drawingTool === "pan") {
                                if (event.button !== 0) return;
                                suppressPanClickRef.current = false;
                                event.preventDefault();
                                panDragRef.current = {
                                    x: event.clientX,
                                    y: event.clientY,
                                    offsetX: zoomOffset.x,
                                    offsetY: zoomOffset.y,
                                };
                                return;
                            }
                            handleScaleMouseDown(event);
                            handleSelectMouseDown(event);
                            handleSectionMouseDown(event);
                            handlePodMouseDown(event);
                        }}
                        onMouseUp={(event) => {
                            if (drawingTool === "pan") {
                                if (panDragRef.current) {
                                    panDragRef.current = null;
                                    suppressPanClickRef.current = true;
                                    setDrawingTool("select");
                                }
                                return;
                            }
                            handleScaleMouseUp(event);
                            handleGPSDragEnd(event);
                            handleSelectMouseUp(event);
                            handleSectionMouseUp(event);
                            handlePodMouseUp(event);
                        }}
                        onMouseMove={(event) => {
                            if (drawingTool === "pan") {
                                if (panDragRef.current) {
                                    setZoomOffset({
                                        x: panDragRef.current.offsetX +
                                            event.clientX - panDragRef.current.x,
                                        y: panDragRef.current.offsetY +
                                            event.clientY - panDragRef.current.y,
                                    });
                                }
                                return;
                            }
                            handleCanvasMouseMove(event);
                            updateParkingPreview(event);
                            handleGPSDragMove(event);
                            handleSelectMouseMove(event);
                            handleSectionMouseMove(event);
                            updateNoParkingPreview(event);
                            updateBuildingPreview(event);
                            updateCrosswalkPreview(event);
                            handlePodMouseMove(event);
                            updateScannerPreview(event);
                            updateOverflowPreview(event);
                        }}
                        onMouseLeave={() => {
                            panDragRef.current = null;
                            setGeofencePreview(null);
                            setNoParkingPreview(null);
                            setBuildingPreview(null);
                            setCrosswalkPreview(null);
                            setOverflowPreview(null);
                        }}
                        className={`relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-lg border border-gray-2 bg-[#2B2B2B] ${
                            drawingTool === "pan"
                                ? "cursor-grab"
                                : getGpsIndex() >= 0 ||
                            setupStep === "geofence" ||
                            setupStep === "scale" ||
                            drawingTool === "parking" ||
                            drawingTool === "section" ||
                            drawingTool === "building" ||
                            drawingTool === "no-parking" ||
                            drawingTool === "crosswalk" ||
                            drawingTool === "pod" ||
                            drawingTool === "scanner" ||
                            drawingTool === "overflow"
                                ? "cursor-crosshair"
                                : ""
                        }`}
                    >

                        {yardImage ? (
                            <div
                                className="relative inline-flex max-h-full max-w-full"
                                style={{
                                    transform: `translate(${zoomOffset.x}px, ${zoomOffset.y}px) scale(${zoom})`,
                                    transformOrigin: "center center",
                                }}
                            >
                                <img
                                    ref={imageRef}
                                    src={yardImage}
                                    alt="Yard"
                                    className="block max-h-full max-w-full object-contain"
                                    draggable={false}
                                />

                                {imageRef.current &&
                                    (geofencePoints.length > 0 ||
                                        (scaleDragStart && scaleDragEnd) ||
                                        parkingAnchor ||
                                        parkingSpaces.length > 0 ||
                                        sections.length > 0 ||
                                        sectionDragStart ||
                                        noParkingZones.length > 0 ||
                                        noParkingPoints.length > 0 ||
                                        buildings.length > 0 ||
                                        buildingPoints.length > 0 ||
                                        crosswalks.length > 0 ||
                                        crosswalkStart ||
                                        pod ||
                                        scanners.length > 0 ||
                                        scannerPlacementAnchor ||
                                        overflowBlocks.length > 0 ||
                                        overflowPoints.length > 0) && (
                                    <svg
                                        className="pointer-events-none absolute inset-0 h-full w-full"
                                        viewBox={`0 0 ${imageRef.current.naturalWidth} ${imageRef.current.naturalHeight}`}
                                        preserveAspectRatio="none"
                                    >
                                        <polyline
                                            points={geofencePoints
                                                .map(([x, y]) => `${x},${y}`)
                                                .join(" ")}
                                            fill="none"
                                            stroke="#DC2626"
                                            strokeWidth="3"
                                            vectorEffect="non-scaling-stroke"
                                        />

                                        {geofenceComplete &&
                                            geofencePoints.length >= 3 && (
                                                <line
                                                    x1={geofencePoints[geofencePoints.length - 1][0]}
                                                    y1={geofencePoints[geofencePoints.length - 1][1]}
                                                    x2={geofencePoints[0][0]}
                                                    y2={geofencePoints[0][1]}
                                                    stroke="#DC2626"
                                                    strokeWidth="3"
                                                    vectorEffect="non-scaling-stroke"
                                                />
                                            )}

                                        {setupStep === "geofence" &&
                                            geofencePreview &&
                                            geofencePoints.length > 0 && (
                                                <line
                                                    x1={geofencePoints[geofencePoints.length - 1][0]}
                                                    y1={geofencePoints[geofencePoints.length - 1][1]}
                                                    x2={geofencePreview[0]}
                                                    y2={geofencePreview[1]}
                                                    stroke="#DC2626"
                                                    strokeWidth="2"
                                                    strokeDasharray="6 4"
                                                    vectorEffect="non-scaling-stroke"
                                                />
                                            )}

                                        {scaleDragStart &&
                                            scaleDragEnd && (
                                                <>
                                                    <line
                                                        x1={scaleDragStart[0]}
                                                        y1={scaleDragStart[1]}
                                                        x2={scaleDragEnd[0]}
                                                        y2={scaleDragEnd[1]}
                                                        stroke="#22D3EE"
                                                        strokeWidth="3"
                                                        strokeDasharray={
                                                            scalePxLength === null
                                                                ? "6 4"
                                                                : undefined
                                                        }
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <circle
                                                        cx={scaleDragStart[0]}
                                                        cy={scaleDragStart[1]}
                                                        r="5"
                                                        fill="#22D3EE"
                                                        stroke="white"
                                                        strokeWidth="2"
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <circle
                                                        cx={scaleDragEnd[0]}
                                                        cy={scaleDragEnd[1]}
                                                        r="5"
                                                        fill="#22D3EE"
                                                        stroke="white"
                                                        strokeWidth="2"
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <text
                                                        x={
                                                            (scaleDragStart[0] +
                                                                scaleDragEnd[0]) /
                                                            2
                                                        }
                                                        y={
                                                            (scaleDragStart[1] +
                                                                scaleDragEnd[1]) /
                                                                2 -
                                                            12
                                                        }
                                                        textAnchor="middle"
                                                        fill="white"
                                                        fontSize="14"
                                                        fontWeight="700"
                                                        stroke="#111827"
                                                        strokeWidth="3"
                                                        paintOrder="stroke"
                                                        vectorEffect="non-scaling-stroke"
                                                    >
                                                        {scalePxLength !== null
                                                            ? `${scaleFeet || "?"} ft`
                                                            : `${Math.hypot(
                                                                  scaleDragEnd[0] -
                                                                      scaleDragStart[0],
                                                                  scaleDragEnd[1] -
                                                                      scaleDragStart[1]
                                                              ).toFixed(1)} px`}
                                                    </text>
                                                </>
                                            )}

                                        {geofencePoints.map(([x, y], index) => {
                                            let tooltip = `Geofence vertex ${index + 1}`;

                                            try {
                                                const anchors = gpsPoints.filter(
                                                    (point): point is GPSPoint => point !== null
                                                );

                                                if (anchors.length === 4) {
                                                    const projection = buildGPSProjection(anchors);
                                                    const [lat, lon] = pixelToGPS([x, y], projection);

                                                    tooltip += ` | Latitude: ${lat.toFixed(8)} | Longitude: ${lon.toFixed(8)}`;
                                                }
                                            } catch {
                                                tooltip += " | GPS calibration unavailable";
                                            }

                                            return (
                                                <circle
                                                    key={index}
                                                    cx={x}
                                                    cy={y}
                                                    r={
                                                        selectedYardObject?.type === "geofence-vertex" &&
                                                        selectedYardObject.id === String(index)
                                                            ? 9
                                                            : 6
                                                    }
                                                    fill={
                                                        selectedYardObject?.type === "geofence-vertex" &&
                                                        selectedYardObject.id === String(index)
                                                            ? "#FACC15"
                                                            : "#DC2626"
                                                    }
                                                    stroke={
                                                        selectedYardObject?.type === "geofence-vertex" &&
                                                        selectedYardObject.id === String(index)
                                                            ? "#111827"
                                                            : "white"
                                                    }
                                                    strokeWidth={
                                                        selectedYardObject?.type === "geofence-vertex" &&
                                                        selectedYardObject.id === String(index)
                                                            ? 3
                                                            : 2
                                                    }
                                                    vectorEffect="non-scaling-stroke"
                                                >
                                                    <title>{tooltip}</title>
                                                </circle>
                                            );
                                        })}

                                        {/* OVERFLOW */}
                                        {overflowBlocks.map((block) => {
                                            const cx =
                                                block.points.reduce(
                                                    (sum, point) =>
                                                        sum + point[0],
                                                    0
                                                ) /
                                                block.points.length;

                                            const cy =
                                                block.points.reduce(
                                                    (sum, point) =>
                                                        sum + point[1],
                                                    0
                                                ) /
                                                block.points.length;

                                            const arrowLength = 40;

                                            const rad =
                                                block.orientationDeg *
                                                Math.PI /
                                                180;

                                            const dx =
                                                Math.sin(rad) *
                                                arrowLength /
                                                2;

                                            const dy =
                                                -Math.cos(rad) *
                                                arrowLength /
                                                2;

                                            const tipX = cx + dx;
                                            const tipY = cy + dy;

                                            return (
                                                <g key={block.id}>
                                                    <polygon
                                                        points={block.points
                                                            .map(
                                                                ([x, y]) =>
                                                                    `${x},${y}`
                                                            )
                                                            .join(" ")}
                                                        fill="none"
                                                        stroke="#FACC15"
                                                        strokeWidth={
                                                            selectedYardObject?.type === "overflow" &&
                                                            selectedYardObject.id === block.id ? 6 : 3
                                                        }
                                                        strokeDasharray={
                                                            block.active
                                                                ? undefined
                                                                : "6 4"
                                                        }
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <line
                                                        x1={cx - dx}
                                                        y1={cy - dy}
                                                        x2={tipX}
                                                        y2={tipY}
                                                        stroke="#FACC15"
                                                        strokeWidth={4}
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <polygon
                                                        points={`
                                                            ${tipX},${tipY}
                                                            ${tipX - 7},${tipY + 12}
                                                            ${tipX + 7},${tipY + 12}
                                                        `}
                                                        fill="#FACC15"
                                                        transform={
                                                            `rotate(${block.orientationDeg} ${tipX} ${tipY})`
                                                        }
                                                    />

                                                    <circle
                                                        cx={tipX}
                                                        cy={tipY}
                                                        r={6}
                                                        fill="#FACC15"
                                                        stroke="black"
                                                        strokeWidth={1}
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <text
                                                        x={cx}
                                                        y={cy}
                                                        textAnchor="middle"
                                                        dominantBaseline="central"
                                                        fill="white"
                                                        fontSize={12}
                                                        fontWeight="700"
                                                    >
                                                        {block.label}
                                                    </text>
                                                </g>
                                            );
                                        })}

                                        {/* OVERFLOW DRAW PREVIEW */}
                                        {drawingTool === "overflow" &&
                                            overflowPoints.length > 0 && (
                                                <>
                                                    <polyline
                                                        points={[
                                                            ...overflowPoints,
                                                            ...(overflowPreview
                                                                ? [overflowPreview]
                                                                : []),
                                                        ]
                                                            .map(
                                                                ([x, y]) =>
                                                                    `${x},${y}`
                                                            )
                                                            .join(" ")}
                                                        fill="none"
                                                        stroke="#FACC15"
                                                        strokeWidth={2}
                                                        strokeDasharray="4 3"
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    {overflowPoints.map(
                                                        ([x, y], index) => (
                                                            <circle
                                                                key={index}
                                                                cx={x}
                                                                cy={y}
                                                                r={3}
                                                                fill="#FACC15"
                                                            />
                                                        )
                                                    )}
                                                </>
                                            )}

                                        {/* SCANNER */}
                                        {scanners.map((scanner) => {
                                            const center = scanner.px;
                                            const angle =
                                                scanner.orientationDeg;

                                            const top =
                                                scannerSquarePoints(
                                                    center,
                                                    -18,
                                                    angle
                                                );

                                            const bottom =
                                                scannerSquarePoints(
                                                    center,
                                                    18,
                                                    angle
                                                );

                                            const stemStart =
                                                scannerRotatedPoint(
                                                    center[0],
                                                    center[1],
                                                    0,
                                                    -10,
                                                    angle
                                                );

                                            const stemEnd =
                                                scannerRotatedPoint(
                                                    center[0],
                                                    center[1],
                                                    0,
                                                    10,
                                                    angle
                                                );

                                            const labelPoint =
                                                scannerRotatedPoint(
                                                    center[0],
                                                    center[1],
                                                    32,
                                                    0,
                                                    angle
                                                );

                                            let scannerTooltip = `Scanner ${scanner.id} | GPS calibration unavailable`;

                                            try {
                                                const anchors = gpsPoints.filter(
                                                    (point): point is GPSPoint => point !== null
                                                );

                                                if (anchors.length === 4) {
                                                    const projection = buildGPSProjection(anchors);
                                                    const [lat, lon] = pixelToGPS(scanner.px, projection);

                                                    scannerTooltip = `Scanner ${scanner.id} | Latitude: ${lat.toFixed(8)} | Longitude: ${lon.toFixed(8)}`;
                                                }
                                            } catch {
                                                // Keep the scanner visible if calibration is unavailable.
                                            }

                                            return (
                                                <g key={scanner.id}>
                                                    <title>{scannerTooltip}</title>
                                                    <polygon
                                                        points={
                                                            scannerPointsString(
                                                                top
                                                            )
                                                        }
                                                        fill={
                                                            selectedYardObject?.type === "scanner" &&
                                                            selectedYardObject.id === scanner.id
                                                                ? "#FACC15" : "#22C55E"
                                                        }
                                                        stroke={
                                                            selectedYardObject?.type === "scanner" &&
                                                            selectedYardObject.id === scanner.id
                                                                ? "#111827" : "white"
                                                        }
                                                        strokeWidth={2}
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <polygon
                                                        points={
                                                            scannerPointsString(
                                                                bottom
                                                            )
                                                        }
                                                        fill={
                                                            selectedYardObject?.type === "scanner" &&
                                                            selectedYardObject.id === scanner.id
                                                                ? "#FACC15" : "#22C55E"
                                                        }
                                                        stroke={
                                                            selectedYardObject?.type === "scanner" &&
                                                            selectedYardObject.id === scanner.id
                                                                ? "#111827" : "white"
                                                        }
                                                        strokeWidth={2}
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <line
                                                        x1={stemStart[0]}
                                                        y1={stemStart[1]}
                                                        x2={stemEnd[0]}
                                                        y2={stemEnd[1]}
                                                        stroke="#22C55E"
                                                        strokeWidth={3}
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <text
                                                        x={labelPoint[0]}
                                                        y={labelPoint[1]}
                                                        textAnchor="middle"
                                                        dominantBaseline="central"
                                                        fill="white"
                                                        fontSize={12}
                                                        fontWeight="700"
                                                    >
                                                        {`Scanner ${scanner.id.replace("SC", "")}`}
                                                    </text>
                                                </g>
                                            );
                                        })}

                                        {/* SCANNER PLACEMENT PREVIEW */}
                                        {scannerPlacementAnchor && (() => {
                                            const center =
                                                scannerPlacementAnchor;

                                            const angle =
                                                scannerPreviewAngle;

                                            const top =
                                                scannerSquarePoints(
                                                    center,
                                                    -18,
                                                    angle
                                                );

                                            const bottom =
                                                scannerSquarePoints(
                                                    center,
                                                    18,
                                                    angle
                                                );

                                            const stemStart =
                                                scannerRotatedPoint(
                                                    center[0],
                                                    center[1],
                                                    0,
                                                    -10,
                                                    angle
                                                );

                                            const stemEnd =
                                                scannerRotatedPoint(
                                                    center[0],
                                                    center[1],
                                                    0,
                                                    10,
                                                    angle
                                                );

                                            return (
                                                <g>
                                                    <polygon
                                                        points={
                                                            scannerPointsString(
                                                                top
                                                            )
                                                        }
                                                        fill="none"
                                                        stroke="#22C55E"
                                                        strokeWidth={2}
                                                        strokeDasharray="4 2"
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <polygon
                                                        points={
                                                            scannerPointsString(
                                                                bottom
                                                            )
                                                        }
                                                        fill="none"
                                                        stroke="#22C55E"
                                                        strokeWidth={2}
                                                        strokeDasharray="4 2"
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <line
                                                        x1={stemStart[0]}
                                                        y1={stemStart[1]}
                                                        x2={stemEnd[0]}
                                                        y2={stemEnd[1]}
                                                        stroke="#22C55E"
                                                        strokeWidth={3}
                                                        strokeDasharray="4 2"
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <circle
                                                        cx={center[0]}
                                                        cy={center[1]}
                                                        r={4}
                                                        fill="#22C55E"
                                                        stroke="white"
                                                        strokeWidth={1}
                                                        vectorEffect="non-scaling-stroke"
                                                    />
                                                </g>
                                            );
                                        })()}

                                        {/* POD */}
                                        {pod && (
                                            <g>
                                                <title>
                                                    {(() => {
                                                        try {
                                                            const anchors = gpsPoints.filter(
                                                                (point): point is GPSPoint => point !== null
                                                            );

                                                            if (anchors.length !== 4) {
                                                                return "POD_HOME | GPS calibration unavailable";
                                                            }

                                                            const projection = buildGPSProjection(anchors);
                                                            const [lat, lon] = pixelToGPS(pod.px, projection);

                                                            return `POD_HOME | Latitude: ${lat.toFixed(8)} | Longitude: ${lon.toFixed(8)}`;
                                                        } catch {
                                                            return "POD_HOME | GPS calibration unavailable";
                                                        }
                                                    })()}
                                                </title>
                                                <rect
                                                    x={pod.px[0] - 12}
                                                    y={pod.px[1] - 12}
                                                    width={24}
                                                    height={24}
                                                    fill={selectedYardObject?.type === "pod" ? "#FACC15" : "#3B82F6"}
                                                    stroke={selectedYardObject?.type === "pod" ? "#111827" : "white"}
                                                    strokeWidth={2}
                                                    vectorEffect="non-scaling-stroke"
                                                />

                                                <text
                                                    x={pod.px[0]}
                                                    y={pod.px[1]}
                                                    textAnchor="middle"
                                                    dominantBaseline="central"
                                                    fill="white"
                                                    fontSize={13}
                                                    fontWeight="700"
                                                >
                                                    P
                                                </text>

                                                <circle
                                                    cx={pod.px[0]}
                                                    cy={pod.px[1]}
                                                    r={4}
                                                    fill="#2563EB"
                                                    stroke="white"
                                                    strokeWidth={2}
                                                    vectorEffect="non-scaling-stroke"
                                                />
                                            </g>
                                        )}

                                        {/* SECTIONS */}
                                        {sections.map((section) => {
                                            const {
                                                x,
                                                y,
                                                width,
                                                height,
                                            } = section.rect;

                                            const cx =
                                                x + width / 2;

                                            const cy =
                                                y + height / 2;

                                            const arrowLength =
                                                Math.min(
                                                    width,
                                                    height
                                                ) * 0.35;

                                            const rad =
                                                section.orientationDeg *
                                                Math.PI /
                                                180;

                                            const dx =
                                                Math.sin(rad) *
                                                arrowLength;

                                            const dy =
                                                -Math.cos(rad) *
                                                arrowLength;

                                            return (
                                                <g key={section.id}>
                                                    <rect
                                                        x={x}
                                                        y={y}
                                                        width={width}
                                                        height={height}
                                                        fill="rgba(59,130,246,0.10)"
                                                        stroke={
                                                            selectedYardObject?.type === "section" &&
                                                            selectedYardObject.id === section.id
                                                                ? "#FACC15"
                                                                : "#3B82F6"
                                                        }
                                                        strokeWidth={
                                                            selectedYardObject?.type === "section" &&
                                                            selectedYardObject.id === section.id
                                                                ? 5
                                                                : 2
                                                        }
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <line
                                                        x1={cx - dx}
                                                        y1={cy - dy}
                                                        x2={cx + dx}
                                                        y2={cy + dy}
                                                        stroke="#2563EB"
                                                        strokeWidth="3"
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <polygon
                                                        points={`
                                                            ${cx + dx},${cy + dy}
                                                            ${cx + dx - 7 * Math.cos(rad)},${cy + dy - 7 * Math.sin(rad)}
                                                            ${cx + dx + 7 * Math.cos(rad)},${cy + dy + 7 * Math.sin(rad)}
                                                        `}
                                                        fill="#2563EB"
                                                    />

                                                    <circle
                                                        cx={section.entry[0]}
                                                        cy={section.entry[1]}
                                                        r="5"
                                                        fill="#F59E0B"
                                                        stroke="white"
                                                        strokeWidth="2"
                                                        vectorEffect="non-scaling-stroke"
                                                    >
                                                        <title>
                                                            {(() => {
                                                                try {
                                                                    const anchors = gpsPoints.filter(
                                                                        (point): point is GPSPoint => point !== null
                                                                    );

                                                                    if (anchors.length !== 4) {
                                                                        return `${section.label} entry | GPS calibration unavailable`;
                                                                    }

                                                                    const projection = buildGPSProjection(anchors);
                                                                    const [lat, lon] = pixelToGPS(section.entry, projection);

                                                                    return `${section.label} entry | Latitude: ${lat.toFixed(8)} | Longitude: ${lon.toFixed(8)}`;
                                                                } catch {
                                                                    return `${section.label} entry | GPS calibration unavailable`;
                                                                }
                                                            })()}
                                                        </title>
                                                    </circle>

                                                    <text
                                                        x={cx}
                                                        y={y + 18}
                                                        textAnchor="middle"
                                                        fill="white"
                                                        fontSize="14"
                                                        fontWeight="700"
                                                        stroke="#111827"
                                                        strokeWidth="3"
                                                        paintOrder="stroke"
                                                        vectorEffect="non-scaling-stroke"
                                                    >
                                                        {section.label}
                                                    </text>
                                                </g>
                                            );
                                        })}

                                        {/* SECTION DRAW PREVIEW */}
                                        {drawingTool === "section" &&
                                            sectionDragStart &&
                                            sectionDragEnd && (
                                                <rect
                                                    x={Math.min(
                                                        sectionDragStart[0],
                                                        sectionDragEnd[0]
                                                    )}
                                                    y={Math.min(
                                                        sectionDragStart[1],
                                                        sectionDragEnd[1]
                                                    )}
                                                    width={Math.abs(
                                                        sectionDragEnd[0] -
                                                        sectionDragStart[0]
                                                    )}
                                                    height={Math.abs(
                                                        sectionDragEnd[1] -
                                                        sectionDragStart[1]
                                                    )}
                                                    fill="rgba(59,130,246,0.08)"
                                                    stroke="#60A5FA"
                                                    strokeWidth="2"
                                                    strokeDasharray="6 4"
                                                    vectorEffect="non-scaling-stroke"
                                                />
                                            )}

                                        {/* NO-PARKING ZONES */}
                                        {/* Buildings */}
                                        {buildings.map((building) => {
                                            const cx =
                                                building.points.reduce(
                                                    (sum, point) =>
                                                        sum + point[0],
                                                    0
                                                ) /
                                                building.points.length;

                                            const cy =
                                                building.points.reduce(
                                                    (sum, point) =>
                                                        sum + point[1],
                                                    0
                                                ) /
                                                building.points.length;

                                            return (
                                                <g key={building.id}>
                                                    <polygon
                                                        points={building.points
                                                            .map(
                                                                ([x, y]) =>
                                                                    `${x},${y}`
                                                            )
                                                            .join(" ")}
                                                        fill="white"
                                                        stroke={
                                                            selectedYardObject?.type === "building" &&
                                                            selectedYardObject.id === building.id
                                                                ? "#FACC15"
                                                                : "black"
                                                        }
                                                        strokeWidth={
                                                            selectedYardObject?.type === "building" &&
                                                            selectedYardObject.id === building.id
                                                                ? 5
                                                                : 2
                                                        }
                                                    />

                                                    <text
                                                        x={cx}
                                                        y={cy}
                                                        fill="black"
                                                        fontSize={14}
                                                        fontWeight="bold"
                                                        textAnchor="middle"
                                                        dominantBaseline="middle"
                                                    >
                                                        {building.label}
                                                    </text>
                                                </g>
                                            );
                                        })}

                                        {/* Building drawing preview */}
                                        {buildingPoints.length > 0 && (
                                            <>
                                                <polyline
                                                    points={[
                                                        ...buildingPoints,
                                                        ...(buildingPreview
                                                            ? [buildingPreview]
                                                            : []),
                                                    ]
                                                        .map(
                                                            ([x, y]) =>
                                                                `${x},${y}`
                                                        )
                                                        .join(" ")}
                                                    fill="none"
                                                    stroke="#4B5563"
                                                    strokeWidth={2}
                                                    strokeDasharray="4 3"
                                                />

                                                {buildingPoints.map(
                                                    ([x, y], index) => (
                                                        <circle
                                                            key={`building-point-${index}`}
                                                            cx={x}
                                                            cy={y}
                                                            r={3}
                                                            fill="#4B5563"
                                                        />
                                                    )
                                                )}
                                            </>
                                        )}

                                        {/* Crosswalks */}
                                        {crosswalks.map((crosswalk) => {
                                            const geometry =
                                                getCrosswalkGeometry(
                                                    crosswalk.p1,
                                                    crosswalk.p2,
                                                    crosswalk.widthFt
                                                );

                                            if (!geometry) {
                                                return null;
                                            }

                                            return (
                                                <g key={crosswalk.id}>
                                                    <polygon
                                                        points={geometry.band
                                                            .map(
                                                                ([x, y]) =>
                                                                    `${x},${y}`
                                                            )
                                                            .join(" ")}
                                                        fill="none"
                                                        stroke={
                                                            selectedYardObject?.type === "crosswalk" &&
                                                            selectedYardObject.id === crosswalk.id
                                                                ? "#111827" : "#FACC15"
                                                        }
                                                        strokeWidth={
                                                            selectedYardObject?.type === "crosswalk" &&
                                                            selectedYardObject.id === crosswalk.id ? 5 : 2
                                                        }
                                                    />

                                                    {geometry.stripes.map(
                                                        (
                                                            stripe,
                                                            index
                                                        ) => (
                                                            <polygon
                                                                key={`${crosswalk.id}-stripe-${index}`}
                                                                points={stripe
                                                                    .map(
                                                                        ([x, y]) =>
                                                                            `${x},${y}`
                                                                    )
                                                                    .join(" ")}
                                                                fill="white"
                                                                stroke="none"
                                                            />
                                                        )
                                                    )}
                                                </g>
                                            );
                                        })}

                                        {/* Crosswalk preview */}
                                        {crosswalkStart &&
                                            crosswalkPreview && (
                                                <line
                                                    x1={crosswalkStart[0]}
                                                    y1={crosswalkStart[1]}
                                                    x2={crosswalkPreview[0]}
                                                    y2={crosswalkPreview[1]}
                                                    stroke="#FACC15"
                                                    strokeWidth={2}
                                                    strokeDasharray="5 3"
                                                />
                                            )}

                                        {noParkingZones.map((zone) => {
                                            const centerX =
                                                zone.points.reduce(
                                                    (sum, point) =>
                                                        sum + point[0],
                                                    0
                                                ) /
                                                zone.points.length;

                                            const centerY =
                                                zone.points.reduce(
                                                    (sum, point) =>
                                                        sum + point[1],
                                                    0
                                                ) /
                                                zone.points.length;

                                            return (
                                                <g key={zone.id}>
                                                    <polygon
                                                        points={zone.points
                                                            .map(
                                                                ([x, y]) =>
                                                                    `${x},${y}`
                                                            )
                                                            .join(" ")}
                                                        fill="rgba(220,38,38,0.25)"
                                                        stroke={
                                                            selectedYardObject?.type === "no-parking" &&
                                                            selectedYardObject.id === zone.id
                                                                ? "#FACC15" : "#DC2626"
                                                        }
                                                        strokeWidth={
                                                            selectedYardObject?.type === "no-parking" &&
                                                            selectedYardObject.id === zone.id ? 5 : 2
                                                        }
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    <text
                                                        x={centerX}
                                                        y={centerY}
                                                        textAnchor="middle"
                                                        dominantBaseline="middle"
                                                        fill="#DC2626"
                                                        fontSize="14"
                                                        fontWeight="700"
                                                        stroke="white"
                                                        strokeWidth="3"
                                                        paintOrder="stroke"
                                                        vectorEffect="non-scaling-stroke"
                                                    >
                                                        NO PARKING
                                                    </text>
                                                </g>
                                            );
                                        })}

                                        {/* NO-PARKING DRAW PREVIEW */}
                                        {drawingTool === "no-parking" &&
                                            noParkingPoints.length > 0 && (
                                                <>
                                                    <polyline
                                                        points={[
                                                            ...noParkingPoints,
                                                            ...(noParkingPreview
                                                                ? [noParkingPreview]
                                                                : []),
                                                        ]
                                                            .map(
                                                                ([x, y]) =>
                                                                    `${x},${y}`
                                                            )
                                                            .join(" ")}
                                                        fill="none"
                                                        stroke="#DC2626"
                                                        strokeWidth="2"
                                                        strokeDasharray="6 4"
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    {noParkingPoints.map(
                                                        ([x, y], index) => (
                                                            <circle
                                                                key={index}
                                                                cx={x}
                                                                cy={y}
                                                                r="5"
                                                                fill="#DC2626"
                                                                stroke="white"
                                                                strokeWidth="2"
                                                                vectorEffect="non-scaling-stroke"
                                                            />
                                                        )
                                                    )}
                                                </>
                                            )}

                                        {/* PARKING SPACES */}
                                        {metersPerPx !== null &&
                                            parkingSpaces.map((space) => {
                                                const pxPerFt =
                                                    0.3048 / metersPerPx;

                                                const lengthPx =
                                                    space.lengthFt * pxPerFt;

                                                const widthPx =
                                                    space.widthFt * pxPerFt;

                                                const rad =
                                                    space.angle *
                                                    Math.PI / 180;

                                                const hx =
                                                    Math.sin(rad);

                                                const hy =
                                                    -Math.cos(rad);

                                                const sx =
                                                    Math.cos(rad);

                                                const sy =
                                                    Math.sin(rad);

                                                /*
                                                 * Anchor is the FRONT CENTER
                                                 * of the parking space.
                                                 *
                                                 * Trailer box extends backward
                                                 * from the anchor 53 ft.
                                                 */
                                                const backX =
                                                    space.anchor[0] -
                                                    hx * lengthPx;

                                                const backY =
                                                    space.anchor[1] -
                                                    hy * lengthPx;

                                                const p1x =
                                                    space.anchor[0] +
                                                    sx * widthPx / 2;

                                                const p1y =
                                                    space.anchor[1] +
                                                    sy * widthPx / 2;

                                                const p2x =
                                                    space.anchor[0] -
                                                    sx * widthPx / 2;

                                                const p2y =
                                                    space.anchor[1] -
                                                    sy * widthPx / 2;

                                                const p3x =
                                                    backX -
                                                    sx * widthPx / 2;

                                                const p3y =
                                                    backY -
                                                    sy * widthPx / 2;

                                                const p4x =
                                                    backX +
                                                    sx * widthPx / 2;

                                                const p4y =
                                                    backY +
                                                    sy * widthPx / 2;

                                                let parkingTooltip = `${space.label} | GPS unavailable`;

                                                try {
                                                    const anchors = gpsPoints.filter(
                                                        (point): point is GPSPoint => point !== null
                                                    );

                                                    if (anchors.length === 4) {
                                                        const projection = buildGPSProjection(anchors);

                                                        const centerX = (space.anchor[0] + backX) / 2;
                                                        const centerY = (space.anchor[1] + backY) / 2;

                                                        const [lat, lon] = pixelToGPS(
                                                            [centerX, centerY],
                                                            projection
                                                        );

                                                        parkingTooltip = `${space.label} | Latitude: ${lat.toFixed(8)} | Longitude: ${lon.toFixed(8)}`;
                                                    }
                                                } catch {
                                                    parkingTooltip = `${space.label} | GPS calibration unavailable`;
                                                }

                                                return (
                                                    <g key={space.id}>
                                                        <title>{parkingTooltip}</title>
                                                        <polygon
                                                            points={`${p1x},${p1y} ${p2x},${p2y} ${p3x},${p3y} ${p4x},${p4y}`}
                                                            fill="rgba(34,197,94,0.16)"
                                                            stroke={
                                                                selectedParkingIds.includes(space.id)
                                                                    ? "#FACC15"
                                                                    : "#22C55E"
                                                            }
                                                            strokeWidth={
                                                                selectedParkingIds.includes(space.id)
                                                                    ? "4"
                                                                    : "2"
                                                            }
                                                            vectorEffect="non-scaling-stroke"
                                                        />

                                                        <circle
                                                            cx={space.anchor[0]}
                                                            cy={space.anchor[1]}
                                                            r="4"
                                                            fill="#22C55E"
                                                            stroke="white"
                                                            strokeWidth="2"
                                                            vectorEffect="non-scaling-stroke"
                                                        />

                                                        <text
                                                            x={
                                                                space.anchor[0] -
                                                                hx * lengthPx / 2
                                                            }
                                                            y={
                                                                space.anchor[1] -
                                                                hy * lengthPx / 2
                                                            }
                                                            textAnchor="middle"
                                                            dominantBaseline="middle"
                                                            fill="white"
                                                            fontSize="12"
                                                            fontWeight="700"
                                                            stroke="#111827"
                                                            strokeWidth="3"
                                                            paintOrder="stroke"
                                                            vectorEffect="non-scaling-stroke"
                                                        >
                                                            {space.label}
                                                        </text>
                                                    </g>
                                                );
                                            })}

                                        {/* PS1 ANCHOR */}
                                        {drawingTool === "parking" &&
                                            parkingAnchor && (
                                                <circle
                                                    cx={parkingAnchor[0]}
                                                    cy={parkingAnchor[1]}
                                                    r="6"
                                                    fill="#22C55E"
                                                    stroke="white"
                                                    strokeWidth="2"
                                                    vectorEffect="non-scaling-stroke"
                                                />
                                            )}

                                        {/* HEADING PREVIEW */}
                                        {drawingTool === "parking" &&
                                            parkingStage === 1 &&
                                            parkingAnchor &&
                                            parkingPreviewAngle !== null && (
                                                <line
                                                    x1={parkingAnchor[0]}
                                                    y1={parkingAnchor[1]}
                                                    x2={
                                                        parkingAnchor[0] +
                                                        Math.sin(
                                                            parkingPreviewAngle *
                                                            Math.PI / 180
                                                        ) * 70
                                                    }
                                                    y2={
                                                        parkingAnchor[1] -
                                                        Math.cos(
                                                            parkingPreviewAngle *
                                                            Math.PI / 180
                                                        ) * 70
                                                    }
                                                    stroke="#FACC15"
                                                    strokeWidth="4"
                                                    vectorEffect="non-scaling-stroke"
                                                />
                                            )}

                                        {/* PS1 -> PS2 SPACING */}
                                        {drawingTool === "parking" &&
                                            parkingStage === 2 &&
                                            parkingAnchor &&
                                            parkingRowPreview && (
                                                <line
                                                    x1={parkingAnchor[0]}
                                                    y1={parkingAnchor[1]}
                                                    x2={parkingRowPreview[0]}
                                                    y2={parkingRowPreview[1]}
                                                    stroke="#22C55E"
                                                    strokeWidth="3"
                                                    strokeDasharray="6 3"
                                                    vectorEffect="non-scaling-stroke"
                                                />
                                            )}

                                        {/* LOCKED SPACING */}
                                        {drawingTool === "parking" &&
                                            parkingStage === 3 &&
                                            parkingAnchor &&
                                            parkingSpacingPoint && (
                                                <line
                                                    x1={parkingAnchor[0]}
                                                    y1={parkingAnchor[1]}
                                                    x2={parkingSpacingPoint[0]}
                                                    y2={parkingSpacingPoint[1]}
                                                    stroke="#22C55E"
                                                    strokeWidth="3"
                                                    strokeDasharray="6 3"
                                                    vectorEffect="non-scaling-stroke"
                                                />
                                            )}

                                        {/* ROW END PREVIEW */}
                                        {drawingTool === "parking" &&
                                            parkingStage === 3 &&
                                            parkingAnchor &&
                                            parkingRowPreview && (
                                                <line
                                                    x1={parkingAnchor[0]}
                                                    y1={parkingAnchor[1]}
                                                    x2={parkingRowPreview[0]}
                                                    y2={parkingRowPreview[1]}
                                                    stroke="#22D3EE"
                                                    strokeWidth="3"
                                                    strokeDasharray="7 4"
                                                    vectorEffect="non-scaling-stroke"
                                                />
                                            )}

                                        {/* DRAG SELECTION RECTANGLE */}
                                        {drawingTool === "select" &&
                                            selectionBox && (
                                                <rect
                                                    x={Math.min(
                                                        selectionBox.start[0],
                                                        selectionBox.end[0]
                                                    )}
                                                    y={Math.min(
                                                        selectionBox.start[1],
                                                        selectionBox.end[1]
                                                    )}
                                                    width={Math.abs(
                                                        selectionBox.end[0] -
                                                        selectionBox.start[0]
                                                    )}
                                                    height={Math.abs(
                                                        selectionBox.end[1] -
                                                        selectionBox.start[1]
                                                    )}
                                                    fill="rgba(34,211,238,0.12)"
                                                    stroke="#22D3EE"
                                                    strokeWidth="2"
                                                    strokeDasharray="7 4"
                                                    vectorEffect="non-scaling-stroke"
                                                />
                                            )}

                                    </svg>
                                )}

                                {gpsPoints.map((point, index) => {
                                    if (!point || !imageRef.current) {
                                        return null;
                                    }

                                    const left =
                                        (point.x / imageRef.current.naturalWidth) * 100;

                                    const top =
                                        (point.y / imageRef.current.naturalHeight) * 100;

                                    return (
                                        <div
                                            key={index}
                                            className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2"
                                            style={{
                                                left: `${left}%`,
                                                top: `${top}%`,
                                            }}
                                        >
                                            <div
                                                className={`pointer-events-auto flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold shadow ${
                                                    selectedYardObject?.type === "gps-point" &&
                                                    selectedYardObject.id === String(index)
                                                        ? "border-[3px] border-yellow-400 bg-yellow-500 text-black ring-2 ring-yellow-300"
                                                        : "border-2 border-white bg-blue text-white"
                                                }`}
                                                onMouseDown={(event) => {
                                                    if (!setupComplete || drawingTool !== "select" || event.button !== 0) return;
                                                    const original = gpsPoints.filter((p): p is GPSPoint => p !== null);
                                                    if (original.length !== 4) return;
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    selectionStartRef.current = null;
                                                    setSelectionBox(null);
                                                    gpsDragRef.current = { index, original };
                                                    setSelectedYardObject({ type: "gps-point", id: String(index) });
                                                    setSelectedParkingIds([]);
                                                }}
                                                title={`P${index + 1} | Latitude: ${point.lat.toFixed(8)} | Longitude: ${point.lon.toFixed(8)}`}
                                            >
                                                P{index + 1}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center">
                                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-white/10">
                                    <Icon
                                        icon="material-symbols:add-photo-alternate-outline"
                                        width={28}
                                        height={28}
                                        className="text-white"
                                    />
                                </div>

                                <p className="font-[Geist-SemiBold] text-white">
                                    No Yard Image Loaded
                                </p>

                                <p className="mt-1 text-sm text-gray-400">
                                    Select Load Image to begin
                                </p>
                            </div>
                        )}

                        {/* Canvas controls */}
                        <div className="absolute bottom-4 right-4 flex gap-2">
                            <CanvasButton
                                icon="material-symbols:add"
                                disabled={!imageLoaded}
                            />
                            <CanvasButton
                                icon="material-symbols:remove"
                                disabled={!imageLoaded}
                            />
                            <CanvasButton
                                icon="material-symbols:center-focus-strong"
                                disabled={!imageLoaded}
                            />
                        </div>
                    </div>

                    {/* Status Bar */}
                    <div className="mt-2 flex items-center justify-between rounded bg-white px-4 py-2 text-xs text-gray-2">
                        <span>
                            Status:{" "}
                            {!imageLoaded
                                ? "Waiting for yard image"
                                : setupComplete
                                ? "Setup complete"
                                : setupStep === "ready"
                                ? "Ready to start setup wizard"
                                : `Setup Wizard - ${getCurrentTitle()}`}
                        </span>

                        <span>
                            {yardImageName
                                ? `Image: ${yardImageName}`
                                : "Zoom: 100%"}
                        </span>
                    </div>
                </div>

                {/* Right Control Panel */}
                <aside className="w-[340px] shrink-0 overflow-y-auto border-l bg-white">

                    <Panel title="Setup Progress">
                        {setupSteps.map((step, index) => (
                            <ProgressItem
                                key={step.key}
                                number={step.number}
                                label={step.label}
                                active={index === currentProgressIndex}
                                complete={
                                    setupStep === "ready"
                                        ? index === 0
                                        : index < currentProgressIndex
                                }
                            />
                        ))}
                    </Panel>

                    <Panel title="Yard Information">

                        <label className="mb-3 block">
                            <span className="mb-1 block text-xs font-[Geist-SemiBold] text-gray-2">
                                Yard Name
                            </span>

                            <input
                                type="text"
                                value={yardInfo.name}
                                onChange={(event) =>
                                    setYardInfo({
                                        ...yardInfo,
                                        name: event.target.value,
                                    })
                                }
                                placeholder="Enter yard name"
                                className="w-full rounded-lg border border-gray-3 px-3 py-2 text-sm outline-none focus:border-green"
                            />
                        </label>

                        <label className="mb-3 block">
                            <span className="mb-1 block text-xs font-[Geist-SemiBold] text-gray-2">
                                Address
                            </span>

                            <input
                                type="text"
                                value={yardInfo.address}
                                onChange={(event) =>
                                    setYardInfo({
                                        ...yardInfo,
                                        address: event.target.value,
                                    })
                                }
                                placeholder="Enter address"
                                className="w-full rounded-lg border border-gray-3 px-3 py-2 text-sm outline-none focus:border-green"
                            />
                        </label>

                        <label className="mb-3 block">
                            <span className="mb-1 block text-xs font-[Geist-SemiBold] text-gray-2">
                                ZIP Code
                            </span>

                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={5}
                                value={yardInfo.zipCode}
                                onChange={(event) =>
                                    setYardInfo({
                                        ...yardInfo,
                                        zipCode: event.target.value,
                                    })
                                }
                                placeholder="Enter ZIP code"
                                className="w-full rounded-lg border border-gray-3 px-3 py-2 text-sm outline-none focus:border-green"
                            />
                        </label>

                        <div className="mt-4 border-t border-gray-3 pt-4">

                            <p className="mb-3 text-xs font-[Geist-SemiBold] text-gray-2">
                                Altitude Limits
                            </p>

                            <label className="mb-3 block">
                                <span className="mb-1 block text-xs text-gray-2">
                                    Altitude A — Transit Height (ft)
                                </span>

                                <input
                                    type="number"
                                    min="15"
                                    max="500"
                                    step="1"
                                    value={yardInfo.altitudeA}
                                    onChange={(event) =>
                                        setYardInfo({
                                            ...yardInfo,
                                            altitudeA: event.target.value,
                                        })
                                    }
                                    className="w-full rounded-lg border border-gray-3 px-3 py-2 text-sm outline-none focus:border-green"
                                />
                            </label>

                            <label className="mb-3 block">
                                <span className="mb-1 block text-xs text-gray-2">
                                    Altitude B — Max Height (ft)
                                </span>

                                <input
                                    type="number"
                                    min="15"
                                    max="500"
                                    step="1"
                                    value={yardInfo.altitudeB}
                                    onChange={(event) =>
                                        setYardInfo({
                                            ...yardInfo,
                                            altitudeB: event.target.value,
                                        })
                                    }
                                    className="w-full rounded-lg border border-gray-3 px-3 py-2 text-sm outline-none focus:border-green"
                                />
                            </label>

                            <label className="mb-3 block">
                                <span className="mb-1 block text-xs text-gray-2">
                                    Altitude C — Min Height (ft)
                                </span>

                                <input
                                    type="number"
                                    min="2"
                                    max="15"
                                    step="0.5"
                                    value={yardInfo.altitudeC}
                                    onChange={(event) =>
                                        setYardInfo({
                                            ...yardInfo,
                                            altitudeC: event.target.value,
                                        })
                                    }
                                    className="w-full rounded-lg border border-gray-3 px-3 py-2 text-sm outline-none focus:border-green"
                                />
                            </label>

                            <p className="text-[11px] leading-4 text-gray-2">
                                A = transit height, B = maximum height,
                                C = minimum operating height.
                            </p>
                        </div>

                        {yardInfoError && (
                            <div className="mt-3 rounded-lg bg-red-50 p-3 text-xs text-red-600">
                                {yardInfoError}
                            </div>
                        )}

                        {setupStep === "yard-info" && (
                            <button
                                type="button"
                                onClick={continueFromYardInfo}
                                className="mt-4 w-full rounded-lg bg-green px-4 py-2 text-sm font-[Geist-SemiBold] text-white"
                            >
                                Continue to GPS Setup
                            </button>
                        )}

                    </Panel>

                    {setupStep === "geofence" && (
                        <Panel title="Geofence">
                            <div className="rounded-lg border border-green bg-green/5 p-3">
                                <p className="text-sm font-[Geist-SemiBold] text-green">
                                    Draw Yard Boundary
                                </p>

                                <p className="mt-1 text-xs leading-5 text-gray-2">
                                    Click around the yard boundary to place geofence points.
                                    Add at least 3 points, then double-click to finish.
                                </p>
                            </div>

                            <div className="mt-3 flex items-center justify-between">
                                <span className="text-sm text-gray-2">
                                    Points placed
                                </span>

                                <span className="text-sm font-[Geist-SemiBold] text-black">
                                    {geofencePoints.length}
                                </span>
                            </div>

                            {geofencePoints.length >= 3 && (
                                <button
                                    type="button"
                                    onClick={finishGeofence}
                                    className="mt-3 w-full rounded-lg bg-green px-3 py-2 text-sm font-[Geist-SemiBold] text-white"
                                >
                                    Finish Geofence
                                </button>
                            )}

                            {geofencePoints.length > 0 && (
                                <button
                                    type="button"
                                    onClick={resetGeofence}
                                    className="mt-2 w-full rounded-lg border border-gray-3 bg-white px-3 py-2 text-sm font-[Geist-SemiBold] text-gray-2"
                                >
                                    Reset Geofence
                                </button>
                            )}

                            {geofenceError && (
                                <div className="mt-3 rounded-lg bg-red-50 p-3 text-xs text-red-600">
                                    {geofenceError}
                                </div>
                            )}
                        </Panel>
                    )}

                    {setupStep === "scale" && (
                        <Panel title="Trailer Measurement">
                            <div className="rounded-lg border border-green bg-green/5 p-3">
                                <p className="text-sm font-[Geist-SemiBold] text-green">
                                    Measure the Trailer
                                </p>

                                <p className="mt-1 text-xs leading-5 text-gray-2">
                                    Click and drag along the full length of a trailer.
                                    The measurement automatically snaps to the nearest
                                    45-degree direction.
                                </p>
                            </div>

                            <label className="mt-4 block">
                                <span className="mb-1 block text-xs text-gray-2">
                                    Trailer Length (ft)
                                </span>

                                <input
                                    type="number"
                                    min="0.01"
                                    step="any"
                                    value={scaleFeet}
                                    onChange={(event) => {
                                        setScaleFeet(event.target.value);
                                        setScaleError("");
                                    }}
                                    className="w-full rounded-lg border border-gray-3 px-3 py-2 text-sm outline-none focus:border-green"
                                />
                            </label>

                            {scalePxLength !== null && (
                                <div className="mt-3 rounded-lg border border-gray-3 bg-gray-4 p-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-2">
                                            Measured Pixels
                                        </span>

                                        <span className="text-sm font-[Geist-SemiBold] text-black">
                                            {scalePxLength.toFixed(1)} px
                                        </span>
                                    </div>
                                </div>
                            )}

                            {scalePxLength !== null &&
                                Number(scaleFeet) > 0 && (
                                    <div className="mt-3 rounded-lg border border-green bg-green/5 p-3">
                                        <p className="mb-3 text-sm font-[Geist-SemiBold] text-green">
                                            Geofence Area
                                        </p>

                                        {(() => {
                                            const previewMetersPerPx =
                                                (Number(scaleFeet) * 0.3048) /
                                                scalePxLength;

                                            const areaM2 =
                                                geofenceAreaPx *
                                                previewMetersPerPx *
                                                previewMetersPerPx;

                                            const areaFt2 =
                                                areaM2 * 10.7639104167;

                                            const acres =
                                                areaFt2 / 43560;

                                            return (
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-gray-2">
                                                            Square Feet
                                                        </span>

                                                        <span className="text-sm font-[Geist-SemiBold] text-black">
                                                            {areaFt2.toLocaleString(
                                                                undefined,
                                                                {
                                                                    maximumFractionDigits: 0,
                                                                }
                                                            )}{" "}
                                                            ft²
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-gray-2">
                                                            Acres
                                                        </span>

                                                        <span className="text-sm font-[Geist-SemiBold] text-black">
                                                            {acres.toFixed(2)} acres
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-gray-2">
                                                            Square Meters
                                                        </span>

                                                        <span className="text-sm font-[Geist-SemiBold] text-black">
                                                            {areaM2.toLocaleString(
                                                                undefined,
                                                                {
                                                                    maximumFractionDigits: 0,
                                                                }
                                                            )}{" "}
                                                            m²
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                            {scalePxLength !== null && (
                                <button
                                    type="button"
                                    onClick={confirmScaleCalibration}
                                    className="mt-3 w-full rounded-lg bg-green px-3 py-2 text-sm font-[Geist-SemiBold] text-white"
                                >
                                    Confirm Trailer Measurement
                                </button>
                            )}

                            {scaleDragStart && (
                                <button
                                    type="button"
                                    onClick={resetScaleCalibration}
                                    className="mt-2 w-full rounded-lg border border-gray-3 bg-white px-3 py-2 text-sm font-[Geist-SemiBold] text-gray-2"
                                >
                                    Reset Measurement
                                </button>
                            )}

                            {scaleError && (
                                <div className="mt-3 rounded-lg bg-red-50 p-3 text-xs text-red-600">
                                    {scaleError}
                                </div>
                            )}
                        </Panel>
                    )}

                    {getGpsIndex() >= 0 && (
                        <Panel title="GPS Coordinates">
                            <div className="mb-3 rounded-lg border border-green bg-green/5 p-3">
                                <p className="text-sm font-[Geist-SemiBold] text-green">
                                    Currently setting: P{getGpsIndex() + 1}
                                </p>

                                <p className="mt-1 text-xs text-gray-2">
                                    Enter the coordinates, then click the corresponding location on the image.
                                </p>
                            </div>

                            {gpsInputs.map((entry, index) => {
                                const active = index === getGpsIndex();
                                const point = gpsPoints[index];

                                return (
                                    <div
                                        key={index}
                                        className={`mb-3 rounded-lg border p-3 ${
                                            active
                                                ? "border-green"
                                                : "border-gray-3"
                                        }`}
                                    >
                                        <div className="mb-2 flex items-center justify-between">
                                            <span className="text-sm font-[Geist-SemiBold] text-black">
                                                Point P{index + 1}
                                            </span>

                                            <span
                                                className={`text-xs ${
                                                    point
                                                        ? "text-green"
                                                        : "text-gray-2"
                                                }`}
                                            >
                                                {point ? "Set" : "Not set"}
                                            </span>
                                        </div>

                                        <label className="mb-2 block">
                                            <span className="mb-1 block text-xs text-gray-2">
                                                Latitude
                                            </span>

                                            <input
                                                type="number"
                                                step="any"
                                                value={entry.lat}
                                                disabled={!active}
                                                onChange={(event) =>
                                                    updateGpsInput(
                                                        index,
                                                        "lat",
                                                        event.target.value
                                                    )
                                                }
                                                className="w-full rounded-lg border border-gray-3 px-3 py-2 text-sm outline-none focus:border-green disabled:bg-gray-4"
                                            />
                                        </label>

                                        <label className="block">
                                            <span className="mb-1 block text-xs text-gray-2">
                                                Longitude
                                            </span>

                                            <input
                                                type="number"
                                                step="any"
                                                value={entry.lon}
                                                disabled={!active}
                                                onChange={(event) =>
                                                    updateGpsInput(
                                                        index,
                                                        "lon",
                                                        event.target.value
                                                    )
                                                }
                                                className="w-full rounded-lg border border-gray-3 px-3 py-2 text-sm outline-none focus:border-green disabled:bg-gray-4"
                                            />
                                        </label>

                                        {point && (
                                            <div className="mt-2 flex items-center justify-between">
                                                <span className="text-xs text-green">
                                                    ✓ P{index + 1} set
                                                </span>

                                                <button
                                                    onClick={() =>
                                                        resetGpsPoint(index)
                                                    }
                                                    className="text-xs font-[Geist-SemiBold] text-red-500"
                                                >
                                                    Reset P{index + 1}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            <button
                                type="button"
                                onClick={loadSampleCoordinates}
                                className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border border-gray-3 bg-white px-3 py-2 text-sm font-[Geist-SemiBold] text-gray-2 transition hover:bg-gray-4"
                            >
                                <Icon
                                    icon="material-symbols:science-outline"
                                    width={17}
                                    height={17}
                                />
                                Load Sample Coordinates
                            </button>

                            <p className="mb-3 text-center text-[11px] text-gray-2">
                                Development testing only
                            </p>

                            {gpsError && (
                                <div className="rounded-lg bg-red-50 p-3 text-xs text-red-600">
                                    {gpsError}
                                </div>
                            )}
                        </Panel>
                    )}

                    {wizardStarted && !setupComplete && (
                        <Panel title="Setup Wizard">
                            <div className="rounded-lg bg-gray-4 p-3">
                                <p className="text-sm font-[Geist-SemiBold] text-black">
                                    {getCurrentTitle()}
                                </p>

                                <p className="mt-1 text-xs leading-5 text-gray-2">
                                    {getCurrentDescription()}
                                </p>
                            </div>
                        </Panel>
                    )}

                    <Panel title="Layers">
                        <Layer label="Geofence" disabled={!setupComplete} />
                        <Layer label="Sections" disabled={!setupComplete} />
                        <Layer label="Measurements" disabled={!setupComplete} />
                        <Layer label="Buildings" disabled={!setupComplete} />
                        <Layer label="Pod" disabled={!setupComplete} />
                        <Layer label="Scanner" disabled={!setupComplete} />
                        <Layer label="Parking" disabled={!setupComplete} />
                        <Layer label="Parking Spaces" disabled={!setupComplete} />
                        <Layer label="No-Parking" disabled={!setupComplete} />
                        <Layer label="Crosswalks" disabled={!setupComplete} />
                    </Panel>

                </aside>
            </div>

            {showDeleteYardDialog && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4">
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="delete-yard-title"
                        className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
                    >
                        <h2 id="delete-yard-title" className="text-xl font-bold text-red-600">
                            Delete Yard
                        </h2>
                        <p className="mt-3 text-sm text-gray-700">
                            Permanently delete setup.json and parking.json for:
                        </p>
                        <p className="mt-2 break-words font-bold text-gray-900">
                            {yardInfo.name}
                        </p>
                        <p className="mt-4 text-sm text-gray-700">
                            Type DELETE exactly to confirm. This action cannot be undone.
                        </p>
                        <input
                            type="text"
                            value={deleteYardConfirmation}
                            onChange={(event) => setDeleteYardConfirmation(event.target.value)}
                            disabled={isDeletingYard}
                            autoComplete="off"
                            placeholder="Type DELETE"
                            className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                        />
                        <div className="mt-5 flex justify-end gap-3">
                            <button
                                type="button"
                                disabled={isDeletingYard}
                                onClick={() => {
                                    setShowDeleteYardDialog(false);
                                    setDeleteYardConfirmation("");
                                }}
                                className="rounded-lg border px-4 py-2 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleDeleteYard}
                                disabled={deleteYardConfirmation !== "DELETE" || isDeletingYard}
                                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {isDeletingYard ? "Deleting..." : "Permanently Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------- Small UI Components ---------- */

function ToolButton({
    icon,
    label,
    disabled = false,
    active = false,
    onClick,
}: {
    icon: string;
    label: string;
    disabled?: boolean;
    active?: boolean;
    onClick?: () => void;
}) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition ${
                disabled
                    ? "cursor-not-allowed border-gray-3 bg-gray-4 text-gray-300 opacity-50"
                    : active
                        ? "border-gray-2 bg-gray-2 text-white"
                        : "border-gray-3 bg-white text-gray-2 hover:bg-gray-4"
            }`}
        >
            <Icon icon={icon} width={16} height={16} />
            <span className="font-[Geist-SemiBold]">{label}</span>
        </button>
    );
}

function Divider() {
    return <div className="mx-1 h-7 w-px shrink-0 bg-gray-3" />;
}

function CanvasButton({
    icon,
    disabled = false,
}: {
    icon: string;
    disabled?: boolean;
}) {
    return (
        <button
            disabled={disabled}
            className={`flex h-9 w-9 items-center justify-center rounded-lg bg-white shadow ${
                disabled
                    ? "cursor-not-allowed opacity-40"
                    : "hover:bg-gray-4"
            }`}
        >
            <Icon icon={icon} width={19} height={19} />
        </button>
    );
}

function Panel({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) {
    return (
        <div className="border-b p-4">
            <h3 className="mb-3 font-[Geist-Bold] text-black">
                {title}
            </h3>

            {children}
        </div>
    );
}

function ProgressItem({
    number,
    label,
    active = false,
    complete = false,
}: {
    number: string;
    label: string;
    active?: boolean;
    complete?: boolean;
}) {
    return (
        <div className="mb-2 flex items-center gap-3">
            <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                    active || complete
                        ? "bg-green text-white"
                        : "bg-gray-4 text-gray-2"
                }`}
            >
                {complete ? (
                    <Icon
                        icon="material-symbols:check"
                        width={15}
                        height={15}
                    />
                ) : (
                    number
                )}
            </div>

            <span
                className={`text-sm ${
                    active
                        ? "font-[Geist-SemiBold] text-green"
                        : complete
                        ? "text-black"
                        : "text-gray-2"
                }`}
            >
                {label}
            </span>
        </div>
    );
}

function Input({
    label,
    placeholder,
}: {
    label: string;
    placeholder: string;
}) {
    return (
        <label className="mb-3 block">
            <span className="mb-1 block text-xs font-[Geist-SemiBold] text-gray-2">
                {label}
            </span>

            <input
                type="text"
                placeholder={placeholder}
                className="w-full rounded-lg border border-gray-3 px-3 py-2 text-sm outline-none focus:border-green"
            />
        </label>
    );
}

function Layer({
    label,
    disabled = false,
}: {
    label: string;
    disabled?: boolean;
}) {
    return (
        <label
            className={`mb-2 flex items-center justify-between ${
                disabled
                    ? "cursor-not-allowed opacity-40"
                    : "cursor-pointer"
            }`}
        >
            <span className="text-sm text-gray-2">{label}</span>

            <input
                type="checkbox"
                defaultChecked
                disabled={disabled}
                className="h-4 w-4 accent-green"
            />
        </label>
    );
}
